/**
 * Lead Builder Page
 * ICP Profile Management, Company Matching, Enrichment Jobs, and Lead Discovery
 *
 * Features:
 * - ICP Profile creation and management with scoring weights
 * - Company match list with tier filtering
 * - Enrichment job management (discovery, scoring)
 * - Intent signal tracking
 * - Contact discovery
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import {
  Target,
  Building2,
  Users,
  Zap,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  Copy,
  Search,
  Filter,
  Download,
  ChevronDown,
  ChevronRight,
  Star,
  TrendingUp,
  Globe,
  Mail,
  Phone,
  Linkedin,
  X,
  Save,
  ArrowLeft,
  Settings,
  Activity,
  Layers,
  UserPlus,
  MapPin,
  Heart,
  ClipboardCheck,
  UserCheck,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  leadBuilderApi,
  coldOutreachApi,
  contactListsApi,
  marketingApi,
  ICPProfile,
  CompanyMatch,
  LeadBuilderJob,
  DiscoveredContact,
  IntentSignal,
  LeadBuilderAnalytics,
} from '../../shared/services/platformApi';
import { DOL5500ImportWizard } from '@/components/integrations/DOL5500ImportWizard';
import { tvTokens, TvPageBar, TvButton } from '../../shared/components/triveny';
import { config as productConfig } from '@/src/config';
import {
  ContactProvenanceBadges,
  ContactReviewActions,
  ContactReviewTab,
} from '../components/lead-builder/ContactProvenance';
import { DecisionMakersTab } from '../components/lead-builder/DecisionMakersTab';

// ============================================================================
// TYPES
// ============================================================================

type LeadBuilderTab = 'icp-profiles' | 'companies' | 'decision-makers' | 'review' | 'jobs' | 'outreach-lists' | 'analytics';

// ============================================================================
// CONSTANTS
// ============================================================================

const INDUSTRIES = [
  'Manufacturing',
  'Logistics & Transportation',
  'Healthcare',
  'Retail',
  'Hospitality',
  'Food Services',
  'Construction',
  'Warehousing',
  'Agriculture',
  'Energy',
  'Utilities',
  'Professional Services',
  'Technology',
  'Financial Services',
];

const INSURANCE_STATUSES = [
  { value: 'self_insured', label: 'Self-Insured' },
  { value: 'level_funded', label: 'Level-Funded' },
  { value: 'fully_insured', label: 'Fully Insured' },
];

const SENIORITY_LEVELS = [
  'C-Level',
  'VP',
  'Director',
  'Manager',
  'Individual Contributor',
];

const DEPARTMENTS = [
  'Human Resources',
  'Benefits',
  'Finance',
  'Operations',
  'Executive',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

// Provider Directory ICP Constants
// Organization types match values in Provider Directory API (NTEE-derived taxonomy)
const ORGANIZATION_TYPES = [
  'Social Services', 'Food Bank', 'Housing Support', 'Mental Health Center',
  'Community Health Center', 'FQHC', 'Healthcare Provider',
  'Homeless Shelter', 'Community Development', 'Education',
  'Employment', 'Youth Development', 'Public Safety',
  'Crime/Legal', 'Voluntary Health', 'Medical Research',
];

// Services match values stored in Provider Directory API
const PROVIDER_SERVICES = [
  'Mental Health Services', 'Counseling', 'Substance Abuse Treatment',
  'Food Assistance', 'Emergency Food', 'Housing Assistance', 'Rental Assistance',
  'Emergency Shelter', 'Primary Care', 'Preventive Care', 'Dental Services',
  'Case Management', 'Referral Services', 'Family Services',
  'Job Training', 'Health Education', 'Crisis Services', 'Transportation',
];

const PROVIDER_DATA_SOURCES = ['HRSA', 'SAMHSA', 'CMS', '211', 'manual', 'irs_propublica'];

// Enrichment / outreach-readiness stage badge (shared by the row + create-list modal)
const getStageBadge = (stage?: string | null): { label: string; cls: string } => {
  switch (stage) {
    case 'outreach_ready': return { label: 'Outreach-ready', cls: 'bg-green-100 text-green-700 border border-green-200' };
    case 'contacts_discovered': return { label: 'Contacts found', cls: 'bg-amber-100 text-amber-700 border border-amber-200' };
    case 'enriched': return { label: 'Domain enriched', cls: 'bg-blue-100 text-blue-700 border border-blue-200' };
    default: return { label: 'Matched', cls: 'bg-gray-100 text-gray-600 border border-gray-200' };
  }
};

// ============================================================================
// ICP PROFILE FORM MODAL
// ============================================================================

interface ICPProfileFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (profile: Partial<ICPProfile>) => void;
  profile?: ICPProfile;
  isLoading: boolean;
}

function ICPProfileFormModal({ isOpen, onClose, onSubmit, profile, isLoading }: ICPProfileFormModalProps) {
  const [formData, setFormData] = useState<Partial<ICPProfile>>({
    name: '',
    description: '',
    profile_type: 'employer',
    target_industries: [],
    excluded_industries: [],
    employee_range_min: 500,
    employee_range_max: 50000,
    revenue_min: undefined,
    revenue_max: undefined,
    insurance_statuses: ['self_insured', 'level_funded'],
    hourly_workforce_pct_min: 50,
    frontline_pct_min: 30,
    turnover_rate_min: 25,
    absenteeism_rate_min: undefined,
    eap_utilization_max: undefined,
    target_titles: [],
    target_seniority_levels: [],
    target_departments: [],
    target_states: [],
    target_metros: [],
    excluded_companies: [],
    weight_industry: 0.25,
    weight_size: 0.20,
    weight_insurance: 0.25,
    weight_workforce: 0.15,
    weight_pain_points: 0.15,
    // Provider ICP defaults
    target_organization_types: [],
    target_services: [],
    target_data_sources: [],
    target_languages: [],
    weight_org_type: 0.25,
    weight_services: 0.30,
    weight_geography: 0.25,
    weight_quality: 0.20,
    is_default: false,
    is_active: true,
  });

  const [step, setStep] = useState(1);
  const totalSteps = 4;

  useEffect(() => {
    if (profile) {
      setFormData(profile);
    } else {
      setFormData({
        name: '',
        description: '',
        profile_type: 'employer',
        target_industries: [],
        excluded_industries: [],
        employee_range_min: 500,
        employee_range_max: 50000,
        insurance_statuses: ['self_insured', 'level_funded'],
        hourly_workforce_pct_min: 50,
        frontline_pct_min: 30,
        turnover_rate_min: 25,
        target_titles: [],
        target_seniority_levels: [],
        target_departments: [],
        target_states: [],
        target_metros: [],
        excluded_companies: [],
        weight_industry: 0.25,
        weight_size: 0.20,
        weight_insurance: 0.25,
        weight_workforce: 0.15,
        weight_pain_points: 0.15,
        target_organization_types: [],
        target_services: [],
        target_data_sources: [],
        target_languages: [],
        weight_org_type: 0.25,
        weight_services: 0.30,
        weight_geography: 0.25,
        weight_quality: 0.20,
        is_default: false,
        is_active: true,
      });
    }
    setStep(1);
  }, [profile, isOpen]);

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const toggleArrayItem = (field: keyof ICPProfile, item: string) => {
    const currentArray = (formData[field] as string[]) || [];
    if (currentArray.includes(item)) {
      setFormData({ ...formData, [field]: currentArray.filter(i => i !== item) });
    } else {
      setFormData({ ...formData, [field]: [...currentArray, item] });
    }
  };

  const isProviderProfile = formData.profile_type === 'provider';
  const totalWeight = isProviderProfile
    ? (formData.weight_org_type || 0) + (formData.weight_services || 0) + (formData.weight_geography || 0) + (formData.weight_quality || 0)
    : (formData.weight_industry || 0) + (formData.weight_size || 0) + (formData.weight_insurance || 0) + (formData.weight_workforce || 0) + (formData.weight_pain_points || 0);
  const weightsValid = Math.abs(totalWeight - 1.0) < 0.01;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{profile ? 'Edit ICP Profile' : 'Create ICP Profile'}</DialogTitle>
          <DialogDescription>
            {isProviderProfile
              ? 'Define criteria for matching providers from your directory (CBOs, clinics, FQHCs).'
              : 'Define your Ideal Customer Profile criteria for targeting self-insured employers with high hourly workforces.'}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s <= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {s}
              </div>
              {s < 4 && (
                <div className={`w-16 h-1 mx-2 ${s < step ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="space-y-4">
          {/* Step 1: Basic Info & Profile Type */}
          {step === 1 && (
            <>
              {/* Profile Type Toggle */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg mb-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, profile_type: 'employer' })}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition flex items-center justify-center gap-2 ${
                    !isProviderProfile ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  Employer ICP
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, profile_type: 'provider' })}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition flex items-center justify-center gap-2 ${
                    isProviderProfile ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Heart className="w-4 h-4" />
                  Provider Directory ICP
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profile Name *</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={isProviderProfile ? 'e.g., Mental Health CBOs in California' : 'e.g., Manufacturing Self-Insured 1000+'}
                  />
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder={isProviderProfile ? 'Describe the provider criteria...' : 'Describe this ICP profile...'}
                  />
                </div>
              </div>

              {!isProviderProfile ? (
                <>
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Company Size</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Min Employees</label>
                        <input
                          type="number"
                          value={formData.employee_range_min || ''}
                          onChange={(e) => setFormData({ ...formData, employee_range_min: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Employees</label>
                        <input
                          type="number"
                          value={formData.employee_range_max || ''}
                          onChange={(e) => setFormData({ ...formData, employee_range_max: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Target Industries</h4>
                    <div className="flex flex-wrap gap-2">
                      {INDUSTRIES.map((industry) => (
                        <button
                          key={industry}
                          type="button"
                          onClick={() => toggleArrayItem('target_industries', industry)}
                          className={`px-3 py-1.5 text-sm rounded-full border ${
                            formData.target_industries?.includes(industry)
                              ? 'bg-blue-100 border-blue-500 text-blue-700'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {industry}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Target Organization Types</h4>
                  <div className="flex flex-wrap gap-2">
                    {ORGANIZATION_TYPES.map((orgType) => (
                      <button
                        key={orgType}
                        type="button"
                        onClick={() => toggleArrayItem('target_organization_types' as keyof ICPProfile, orgType)}
                        className={`px-3 py-1.5 text-sm rounded-full border ${
                          (formData.target_organization_types as string[] || []).includes(orgType)
                            ? 'bg-teal-100 border-teal-500 text-teal-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {orgType}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {(formData.target_organization_types as string[] || []).length} types selected
                  </p>
                </div>
              )}
            </>
          )}

          {/* Step 2: Insurance & Workforce (Employer) / Services & Filters (Provider) */}
          {step === 2 && (
            <>
              {!isProviderProfile ? (
                <>
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-gray-900 mb-3">Insurance Status (Critical for WeaveIn)</h4>
                    <div className="flex flex-wrap gap-3">
                      {INSURANCE_STATUSES.map((status) => (
                        <label key={status.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.insurance_statuses?.includes(status.value)}
                            onChange={() => toggleArrayItem('insurance_statuses', status.value)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{status.label}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Self-insured and level-funded employers are ideal for SDOH solutions as they bear the financial risk.
                    </p>
                  </div>

                  <div className="border-b pb-4">
                    <h4 className="font-medium text-gray-900 mb-3">Workforce Characteristics</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Hourly Workforce % <span className="text-blue-600">(Key Signal)</span>
                        </label>
                        <input
                          type="number"
                          value={formData.hourly_workforce_pct_min || ''}
                          onChange={(e) => setFormData({ ...formData, hourly_workforce_pct_min: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min={0}
                          max={100}
                        />
                        <p className="mt-1 text-xs text-gray-500">Higher hourly % = more SDOH challenges</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Min Frontline %</label>
                        <input
                          type="number"
                          value={formData.frontline_pct_min || ''}
                          onChange={(e) => setFormData({ ...formData, frontline_pct_min: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min={0}
                          max={100}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Turnover Rate % <span className="text-blue-600">(Pain Point)</span>
                        </label>
                        <input
                          type="number"
                          value={formData.turnover_rate_min || ''}
                          onChange={(e) => setFormData({ ...formData, turnover_rate_min: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min={0}
                          max={200}
                        />
                        <p className="mt-1 text-xs text-gray-500">High turnover signals retention challenges</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max EAP Utilization %</label>
                        <input
                          type="number"
                          value={formData.eap_utilization_max || ''}
                          onChange={(e) => setFormData({ ...formData, eap_utilization_max: parseInt(e.target.value) || undefined })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min={0}
                          max={100}
                        />
                        <p className="mt-1 text-xs text-gray-500">Low EAP usage = underutilized benefits</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Target States</h4>
                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 border rounded-lg bg-gray-50">
                      {US_STATES.map((state) => (
                        <button
                          key={state}
                          type="button"
                          onClick={() => toggleArrayItem('target_states', state)}
                          className={`px-2 py-1 text-xs rounded ${
                            formData.target_states?.includes(state)
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {state}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.target_states?.length || 0} states selected {formData.target_states?.length === 0 && '(all states if none selected)'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-gray-900 mb-3">Target Services</h4>
                    <div className="flex flex-wrap gap-2">
                      {PROVIDER_SERVICES.map((svc) => (
                        <button
                          key={svc}
                          type="button"
                          onClick={() => toggleArrayItem('target_services' as keyof ICPProfile, svc)}
                          className={`px-3 py-1.5 text-sm rounded-full border ${
                            (formData.target_services as string[] || []).includes(svc)
                              ? 'bg-purple-100 border-purple-500 text-purple-700'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {svc}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-b pb-4">
                    <h4 className="font-medium text-gray-900 mb-3">Data Sources</h4>
                    <div className="flex flex-wrap gap-3">
                      {PROVIDER_DATA_SOURCES.map((ds) => (
                        <label key={ds} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(formData.target_data_sources as string[] || []).includes(ds)}
                            onChange={() => toggleArrayItem('target_data_sources' as keyof ICPProfile, ds)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{ds}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">Filter providers by their import source.</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Provider Filters</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.provider_accepts_medicaid || false}
                          onChange={(e) => setFormData({ ...formData, provider_accepts_medicaid: e.target.checked || undefined })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Accepts Medicaid</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.provider_accepts_medicare || false}
                          onChange={(e) => setFormData({ ...formData, provider_accepts_medicare: e.target.checked || undefined })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Accepts Medicare</span>
                      </label>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Quality Score (0-5)
                      </label>
                      <input
                        type="number"
                        value={formData.min_quality_score ?? ''}
                        onChange={(e) => setFormData({ ...formData, min_quality_score: e.target.value ? parseFloat(e.target.value) : undefined })}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        min={0}
                        max={5}
                        step={0.5}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Step 3: Target Personas (Employer) / Geography & Languages (Provider) */}
          {step === 3 && (
            <>
              {!isProviderProfile ? (
                <>
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-gray-900 mb-3">Target Seniority Levels</h4>
                    <div className="flex flex-wrap gap-2">
                      {SENIORITY_LEVELS.map((level) => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => toggleArrayItem('target_seniority_levels', level)}
                          className={`px-3 py-1.5 text-sm rounded-full border ${
                            formData.target_seniority_levels?.includes(level)
                              ? 'bg-purple-100 border-purple-500 text-purple-700'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-b pb-4">
                    <h4 className="font-medium text-gray-900 mb-3">Target Departments</h4>
                    <div className="flex flex-wrap gap-2">
                      {DEPARTMENTS.map((dept) => (
                        <button
                          key={dept}
                          type="button"
                          onClick={() => toggleArrayItem('target_departments', dept)}
                          className={`px-3 py-1.5 text-sm rounded-full border ${
                            formData.target_departments?.includes(dept)
                              ? 'bg-green-100 border-green-500 text-green-700'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {dept}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Target Titles (comma-separated)</h4>
                    <textarea
                      value={formData.target_titles?.join(', ') || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        target_titles: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="CHRO, VP of HR, Benefits Director, VP of Total Rewards..."
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Key decision-makers for employee benefits and wellness programs
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-gray-900 mb-3">Target States</h4>
                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 border rounded-lg bg-gray-50">
                      {US_STATES.map((state) => (
                        <button
                          key={state}
                          type="button"
                          onClick={() => toggleArrayItem('target_states', state)}
                          className={`px-2 py-1 text-xs rounded ${
                            formData.target_states?.includes(state)
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {state}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.target_states?.length || 0} states selected {formData.target_states?.length === 0 && '(all states if none selected)'}
                    </p>
                  </div>

                  <div className="border-b pb-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      Geographic Radius Search
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Radius (miles)</label>
                        <input
                          type="number"
                          value={formData.search_radius_miles ?? ''}
                          onChange={(e) => setFormData({ ...formData, search_radius_miles: e.target.value ? parseInt(e.target.value) : undefined })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="50"
                          min={1}
                          max={500}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Center Lat</label>
                        <input
                          type="number"
                          value={formData.search_center_lat ?? ''}
                          onChange={(e) => setFormData({ ...formData, search_center_lat: e.target.value ? parseFloat(e.target.value) : undefined })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="37.7749"
                          step={0.0001}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Center Lon</label>
                        <input
                          type="number"
                          value={formData.search_center_lon ?? ''}
                          onChange={(e) => setFormData({ ...formData, search_center_lon: e.target.value ? parseFloat(e.target.value) : undefined })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="-122.4194"
                          step={0.0001}
                        />
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Optional: providers within this radius will score higher on geography.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Target Languages</h4>
                    <textarea
                      value={(formData.target_languages as string[] || []).join(', ')}
                      onChange={(e) => setFormData({
                        ...formData,
                        target_languages: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="English, Spanish, Mandarin..."
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Comma-separated. Leave blank to include all languages.
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          {/* Step 4: Scoring Weights */}
          {step === 4 && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-blue-900 mb-2">
                  {isProviderProfile ? 'Provider Matching Weights' : 'ICP Scoring Weights'}
                </h4>
                <p className="text-sm text-blue-700">
                  Weights determine how each factor contributes to the overall match score.
                  Total must equal 100%.
                </p>
              </div>

              <div className="space-y-4">
                {!isProviderProfile ? (
                  <>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">Industry Match</label>
                        <span className="text-sm text-gray-500">{((formData.weight_industry || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        value={(formData.weight_industry || 0) * 100}
                        onChange={(e) => setFormData({ ...formData, weight_industry: parseInt(e.target.value) / 100 })}
                        className="w-full"
                        min={0}
                        max={50}
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">Company Size</label>
                        <span className="text-sm text-gray-500">{((formData.weight_size || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        value={(formData.weight_size || 0) * 100}
                        onChange={(e) => setFormData({ ...formData, weight_size: parseInt(e.target.value) / 100 })}
                        className="w-full"
                        min={0}
                        max={50}
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">Insurance Status (Self-Insured)</label>
                        <span className="text-sm text-gray-500">{((formData.weight_insurance || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        value={(formData.weight_insurance || 0) * 100}
                        onChange={(e) => setFormData({ ...formData, weight_insurance: parseInt(e.target.value) / 100 })}
                        className="w-full"
                        min={0}
                        max={50}
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">Workforce Characteristics</label>
                        <span className="text-sm text-gray-500">{((formData.weight_workforce || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        value={(formData.weight_workforce || 0) * 100}
                        onChange={(e) => setFormData({ ...formData, weight_workforce: parseInt(e.target.value) / 100 })}
                        className="w-full"
                        min={0}
                        max={50}
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">Pain Points & Signals</label>
                        <span className="text-sm text-gray-500">{((formData.weight_pain_points || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        value={(formData.weight_pain_points || 0) * 100}
                        onChange={(e) => setFormData({ ...formData, weight_pain_points: parseInt(e.target.value) / 100 })}
                        className="w-full"
                        min={0}
                        max={50}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">Organization Type</label>
                        <span className="text-sm text-gray-500">{((formData.weight_org_type || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        value={(formData.weight_org_type || 0) * 100}
                        onChange={(e) => setFormData({ ...formData, weight_org_type: parseInt(e.target.value) / 100 })}
                        className="w-full"
                        min={0}
                        max={50}
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">Services Match</label>
                        <span className="text-sm text-gray-500">{((formData.weight_services || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        value={(formData.weight_services || 0) * 100}
                        onChange={(e) => setFormData({ ...formData, weight_services: parseInt(e.target.value) / 100 })}
                        className="w-full"
                        min={0}
                        max={50}
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">Geography / Proximity</label>
                        <span className="text-sm text-gray-500">{((formData.weight_geography || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        value={(formData.weight_geography || 0) * 100}
                        onChange={(e) => setFormData({ ...formData, weight_geography: parseInt(e.target.value) / 100 })}
                        className="w-full"
                        min={0}
                        max={50}
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">Quality Score</label>
                        <span className="text-sm text-gray-500">{((formData.weight_quality || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        value={(formData.weight_quality || 0) * 100}
                        onChange={(e) => setFormData({ ...formData, weight_quality: parseInt(e.target.value) / 100 })}
                        className="w-full"
                        min={0}
                        max={50}
                      />
                    </div>
                  </>
                )}

                <div className={`p-3 rounded-lg ${weightsValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${weightsValid ? 'text-green-700' : 'text-red-700'}`}>
                      Total Weight
                    </span>
                    <span className={`font-bold ${weightsValid ? 'text-green-700' : 'text-red-700'}`}>
                      {(totalWeight * 100).toFixed(0)}%
                    </span>
                  </div>
                  {!weightsValid && (
                    <p className="text-sm text-red-600 mt-1">Weights must sum to 100%</p>
                  )}
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Set as default profile for new jobs</span>
                </label>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="mt-6">
          <div className="flex justify-between w-full">
            <div>
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Back
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              {step < totalSteps ? (
                <button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  disabled={step === 1 && !formData.name}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  disabled={isLoading || !weightsValid}
                >
                  {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {profile ? 'Update Profile' : 'Create Profile'}
                </button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ICP PROFILE CARD
// ============================================================================

interface ICPProfileCardProps {
  profile: ICPProfile;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onViewMatches: () => void;
  onRunDiscovery: () => void;
  onDOLImport?: () => void;
  onEnrichContacts?: () => void;
}

function ICPProfileCard({ profile, onEdit, onDelete, onDuplicate, onViewMatches, onRunDiscovery, onDOLImport, onEnrichContacts }: ICPProfileCardProps) {
  const isProvider = profile.profile_type === 'provider';
  return (
    <div className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${isProvider ? 'border-teal-200' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {isProvider
            ? <Heart className={`w-5 h-5 ${profile.is_active ? 'text-teal-600' : 'text-gray-400'}`} />
            : <Target className={`w-5 h-5 ${profile.is_active ? 'text-blue-600' : 'text-gray-400'}`} />
          }
          <h3 className="font-semibold text-gray-900">{profile.name}</h3>
          <span className={`px-2 py-0.5 text-xs rounded-full ${
            isProvider ? 'bg-teal-100 text-teal-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {isProvider ? 'Provider' : 'Employer'}
          </span>
          {profile.is_default && (
            <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">Default</span>
          )}
          {!profile.is_active && (
            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">Inactive</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onViewMatches} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded">
            <Eye className="w-4 h-4" />
          </button>
          <button onClick={onDuplicate} className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded">
            <Copy className="w-4 h-4" />
          </button>
          <button onClick={onEdit} className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded">
            <Edit className="w-4 h-4" />
          </button>
          <button onClick={onDelete} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {profile.description && (
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{profile.description}</p>
      )}

      {!isProvider ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">
                {profile.employee_range_min?.toLocaleString()}-{profile.employee_range_max?.toLocaleString()} employees
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{profile.hourly_workforce_pct_min}%+ hourly</span>
            </div>
          </div>

          {profile.target_industries && profile.target_industries.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {profile.target_industries.slice(0, 3).map((industry) => (
                <span key={industry} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                  {industry}
                </span>
              ))}
              {profile.target_industries.length > 3 && (
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  +{profile.target_industries.length - 3} more
                </span>
              )}
            </div>
          )}

          {profile.insurance_statuses && profile.insurance_statuses.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {profile.insurance_statuses.map((status) => (
                <span key={status} className="px-2 py-0.5 text-xs bg-green-50 text-green-700 rounded">
                  {status.replace('_', '-')}
                </span>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {profile.target_organization_types && (profile.target_organization_types as string[]).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {(profile.target_organization_types as string[]).slice(0, 4).map((ot) => (
                <span key={ot} className="px-2 py-0.5 text-xs bg-teal-50 text-teal-700 rounded">
                  {ot}
                </span>
              ))}
              {(profile.target_organization_types as string[]).length > 4 && (
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  +{(profile.target_organization_types as string[]).length - 4} more
                </span>
              )}
            </div>
          )}

          {profile.target_services && (profile.target_services as string[]).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {(profile.target_services as string[]).slice(0, 3).map((svc) => (
                <span key={svc} className="px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded">
                  {svc}
                </span>
              ))}
              {(profile.target_services as string[]).length > 3 && (
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  +{(profile.target_services as string[]).length - 3} more
                </span>
              )}
            </div>
          )}

          <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
            {profile.provider_accepts_medicaid && <span className="text-green-600">Medicaid</span>}
            {profile.provider_accepts_medicare && <span className="text-blue-600">Medicare</span>}
            {profile.target_states && profile.target_states.length > 0 && (
              <span>{profile.target_states.length} states</span>
            )}
            {profile.search_radius_miles && (
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{profile.search_radius_miles}mi radius</span>
            )}
          </div>
        </>
      )}

      {/* Action buttons */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onRunDiscovery(); }}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border ${
            isProvider
              ? 'text-teal-700 bg-teal-50 hover:bg-teal-100 border-teal-200'
              : 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200'
          }`}
        >
          <Play className="w-3.5 h-3.5" />
          {isProvider ? 'Import Providers' : 'Run Discovery'}
        </button>
        {isProvider && onEnrichContacts && (
          <button
            onClick={(e) => { e.stopPropagation(); onEnrichContacts(); }}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200"
          >
            <Search className="w-3.5 h-3.5" />
            Enrich Contacts
          </button>
        )}
        {!isProvider && onDOLImport && (
          <button
            onClick={(e) => { e.stopPropagation(); onDOLImport(); }}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200"
          >
            <Download className="w-3.5 h-3.5" />
            Import DOL Data
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPANY MATCH ROW
// ============================================================================

interface CompanyMatchRowProps {
  match: CompanyMatch;
  onView: () => void;
  isSelected?: boolean;
  onSelectChange?: (id: string, selected: boolean) => void;
}

function CompanyMatchRow({ match, onView, isSelected, onSelectChange }: CompanyMatchRowProps) {
  const isProviderMatch = match.source_type === 'provider_import';

  const getTierColor = (tier: number) => {
    switch (tier) {
      case 1: return 'bg-green-100 text-green-800 border-green-300';
      case 2: return 'bg-blue-100 text-blue-800 border-blue-300';
      case 3: return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 4: return 'bg-orange-100 text-orange-800 border-orange-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'matched': return 'bg-blue-100 text-blue-700';
      case 'qualified': return 'bg-green-100 text-green-700';
      case 'disqualified': return 'bg-red-100 text-red-700';
      case 'contacted': return 'bg-purple-100 text-purple-700';
      case 'converted': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <tr className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`} onClick={onView}>
      {onSelectChange && (
        <td className="px-2 py-3 w-8" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected || false}
            onChange={(e) => onSelectChange(match.id, e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isProviderMatch ? 'bg-teal-50' : 'bg-gray-100'}`}>
            {isProviderMatch
              ? <Heart className="w-5 h-5 text-teal-500" />
              : <Building2 className="w-5 h-5 text-gray-500" />
            }
          </div>
          <div>
            <div className="font-medium text-gray-900 flex items-center gap-2">
              {match.company_name}
              {isProviderMatch && (
                <span className="px-1.5 py-0.5 text-[10px] bg-teal-100 text-teal-700 rounded">Provider</span>
              )}
            </div>
            {isProviderMatch ? (
              <div className="text-sm text-gray-500">
                {match.organization_type || match.industry || '-'}
                {match.quality_score != null && (
                  <span className="ml-2 text-yellow-600">{'★'.repeat(Math.round(Number(match.quality_score)))}</span>
                )}
              </div>
            ) : (
              match.domain && <div className="text-sm text-gray-500">{match.domain}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 text-xs font-medium rounded border ${getTierColor(match.tier)}`}>
          Tier {match.tier}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{ width: `${match.score * 100}%` }}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">{(match.score * 100).toFixed(0)}%</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {isProviderMatch ? (match.organization_type || '-') : (match.industry || '-')}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {isProviderMatch ? (
          <div className="flex items-center gap-1">
            {match.provider_email && <Mail className="w-3 h-3 text-blue-500" title={match.provider_email} />}
            {match.provider_phone && <Phone className="w-3 h-3 text-green-500" title={match.provider_phone} />}
            {match.distance_miles != null && (
              <span className="text-xs text-gray-400">{Number(match.distance_miles).toFixed(1)}mi</span>
            )}
          </div>
        ) : (
          match.employee_count?.toLocaleString() || '-'
        )}
      </td>
      <td className="px-4 py-3">
        {isProviderMatch ? (
          <div className="flex items-center gap-1">
            {match.accepts_medicaid && <span className="px-1 py-0.5 text-[10px] bg-green-100 text-green-700 rounded">MC</span>}
            {match.accepts_medicare && <span className="px-1 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded">MR</span>}
            {!match.accepts_medicaid && !match.accepts_medicare && <span className="text-gray-300">-</span>}
          </div>
        ) : (
          match.is_self_insured ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <XCircle className="w-4 h-4 text-gray-300" />
          )
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col items-start gap-1">
          {(() => { const b = getStageBadge(match.enrichment_stage); return (
            <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${b.cls}`}>{b.label}</span>
          ); })()}
          {(match.contacts_count ?? 0) > 0 && (
            <span className="text-[11px] text-gray-500">
              {match.contacts_count} contact{match.contacts_count === 1 ? '' : 's'}
              {(match.emailable_contacts_count ?? 0) > 0 && ` · ${match.emailable_contacts_count} w/ email`}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <button
          onClick={(e) => { e.stopPropagation(); onView(); }}
          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
        >
          <Eye className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function LeadBuilderPage() {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const isTriveny = productConfig.isTriveny;
  const [activeTab, setActiveTab] = useState<LeadBuilderTab>('icp-profiles');
  const [reviewCount, setReviewCount] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [icpProfiles, setIcpProfiles] = useState<ICPProfile[]>([]);
  const [companyMatches, setCompanyMatches] = useState<CompanyMatch[]>([]);
  const [jobs, setJobs] = useState<LeadBuilderJob[]>([]);
  const [analytics, setAnalytics] = useState<LeadBuilderAnalytics | null>(null);

  // Modal states
  const [showICPModal, setShowICPModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<ICPProfile | undefined>();
  const [savingProfile, setSavingProfile] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [showDOLImportModal, setShowDOLImportModal] = useState(false);
  const [showAssignIcpModal, setShowAssignIcpModal] = useState(false);
  const [assignIcpTarget, setAssignIcpTarget] = useState<string>('');
  const [assigningIcp, setAssigningIcp] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(new Set());

  // Filters
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [minEmployees, setMinEmployees] = useState<number | null>(null);
  const [maxEmployees, setMaxEmployees] = useState<number | null>(null);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [selfInsuredOnly, setSelfInsuredOnly] = useState(false);
  const [planTypeFilter, setPlanTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // Enrichment/outreach-readiness stage filter + funnel counts
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [stageCounts, setStageCounts] = useState<{ total: number; matched: number; enriched: number; contacts_discovered: number; outreach_ready: number } | null>(null);

  // Debounce search input (400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination (server-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>('score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectingAll, setSelectingAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 25;

  // Company detail
  const [selectedCompany, setSelectedCompany] = useState<CompanyMatch | null>(null);
  const [companyContacts, setCompanyContacts] = useState<DiscoveredContact[]>([]);
  const [isDiscoveringContacts, setIsDiscoveringContacts] = useState(false);
  const [contactDiscoveryResult, setContactDiscoveryResult] = useState<any>(null);
  const [showAddToCampaignModal, setShowAddToCampaignModal] = useState(false);
  const [campaignId, setCampaignId] = useState('');
  // Spine #1: add discovered contacts to a nurture contact list.
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [availableLists, setAvailableLists] = useState<Array<{ id: string; name: string; member_count?: number }>>([]);
  const [addToListId, setAddToListId] = useState('');
  const [newListName, setNewListName] = useState('');
  const [addingToList, setAddingToList] = useState(false);
  const [addToListResult, setAddToListResult] = useState<string | null>(null);
  // Spine: emit lead_builder_match events so configured automations fire.
  const [runningAutomations, setRunningAutomations] = useState(false);
  const [addingToCampaign, setAddingToCampaign] = useState(false);

  // Waterfall config & status
  const [waterfallConfig, setWaterfallConfig] = useState<any>(null);
  const [companyWaterfallStatus, setCompanyWaterfallStatus] = useState<any>(null);
  const [availableCampaigns, setAvailableCampaigns] = useState<any[]>([]);
  const [campaignMode, setCampaignMode] = useState<'existing' | 'new'>('existing');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [newCampaignType, setNewCampaignType] = useState('cold_email');

  // Bulk contact discovery
  const [isBulkDiscovering, setIsBulkDiscovering] = useState(false);
  const [isBackfillingDomains, setIsBackfillingDomains] = useState(false);
  const [bulkDiscoveryResult, setBulkDiscoveryResult] = useState<any>(null);

  // Provider config modal
  const [showProviderConfigModal, setShowProviderConfigModal] = useState(false);
  const [providerToConfig, setProviderToConfig] = useState<string>('');
  const [providerApiKey, setProviderApiKey] = useState('');
  const [providerClientId, setProviderClientId] = useState('');
  const [providerBudget, setProviderBudget] = useState('');
  const [savingProvider, setSavingProvider] = useState(false);

  // Create outreach list modal
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [createListName, setCreateListName] = useState('');
  const [createListDMOnly, setCreateListDMOnly] = useState(false);
  const [createListMinConfidence, setCreateListMinConfidence] = useState(0);
  const [creatingList, setCreatingList] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use allSettled so one failing endpoint doesn't block the others
      const [profilesResult, matchesResult, jobsResult, analyticsResult, stageCountsResult] = await Promise.allSettled([
        leadBuilderApi.getICPProfiles(),
        leadBuilderApi.getCompanyMatches({
          icp_profile_id: selectedProfileId || undefined,
          status: statusFilter || undefined,
          tier: tierFilter || undefined,
          min_employees: minEmployees || undefined,
          max_employees: maxEmployees || undefined,
          state: stateFilter || undefined,
          is_self_insured: selfInsuredOnly ? true : undefined,
          plan_type: planTypeFilter || undefined,
          search: debouncedSearch || undefined,
          stage: stageFilter || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
        }),
        leadBuilderApi.getJobs(),
        leadBuilderApi.getAnalyticsSummary(),
        leadBuilderApi.getCompanyMatchStageCounts({
          icp_profile_id: selectedProfileId || undefined,
          status: statusFilter || undefined,
          tier: tierFilter || undefined,
          min_employees: minEmployees || undefined,
          max_employees: maxEmployees || undefined,
          state: stateFilter || undefined,
          is_self_insured: selfInsuredOnly ? true : undefined,
          plan_type: planTypeFilter || undefined,
          search: debouncedSearch || undefined,
        }),
      ]);

      if (profilesResult.status === 'fulfilled' && profilesResult.value.success && profilesResult.value.data) {
        setIcpProfiles(profilesResult.value.data);
      }
      if (matchesResult.status === 'fulfilled' && matchesResult.value.success && matchesResult.value.data) {
        const data = matchesResult.value.data as any;
        if (data.results && Array.isArray(data.results)) {
          setCompanyMatches(data.results);
          setTotalCount(data.total_count || 0);
        } else if (Array.isArray(data)) {
          setCompanyMatches(data);
          setTotalCount(data.length);
        } else {
          setCompanyMatches([]);
          setTotalCount(0);
        }
      }
      if (jobsResult.status === 'fulfilled' && jobsResult.value.success && jobsResult.value.data) {
        setJobs(jobsResult.value.data);
      }
      if (analyticsResult.status === 'fulfilled' && analyticsResult.value.success && analyticsResult.value.data) {
        setAnalytics(analyticsResult.value.data);
      }
      if (stageCountsResult.status === 'fulfilled' && stageCountsResult.value.success && stageCountsResult.value.data) {
        setStageCounts(stageCountsResult.value.data);
      }

      // Log any failed requests for debugging
      [profilesResult, matchesResult, jobsResult, analyticsResult, stageCountsResult].forEach((r, i) => {
        if (r.status === 'rejected') {
          const names = ['profiles', 'matches', 'jobs', 'analytics', 'stageCounts'];
          console.warn(`Lead Builder: ${names[i]} API failed:`, r.reason);
        }
      });
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading lead builder data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProfileId, statusFilter, tierFilter, minEmployees, maxEmployees, stateFilter, selfInsuredOnly, planTypeFilter, stageFilter, debouncedSearch, currentPage, sortBy, sortDir]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Live-poll jobs while any is queued/running so the Jobs tab shows real progress
  const hasActiveJob = jobs.some(j => j.status === 'queued' || j.status === 'running');
  useEffect(() => {
    if (!hasActiveJob) return;
    const t = setInterval(async () => {
      try {
        const r = await leadBuilderApi.getJobs();
        if (r.success && r.data) setJobs(r.data);
      } catch { /* transient — keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [hasActiveJob]);

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
    setCurrentPage(1);
  };

  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const resp = await leadBuilderApi.getCompanyMatchIds({
        icp_profile_id: selectedProfileId || undefined,
        status: statusFilter || undefined,
        tier: tierFilter || undefined,
        min_employees: minEmployees || undefined,
        max_employees: maxEmployees || undefined,
        state: stateFilter || undefined,
        is_self_insured: selfInsuredOnly ? true : undefined,
        plan_type: planTypeFilter || undefined,
        search: debouncedSearch || undefined,
        stage: stageFilter || undefined,
      });
      if (resp.success && resp.data) {
        setSelectedCompanyIds(new Set(resp.data.ids));
        if (resp.data.capped) alert(`Selected the first ${resp.data.count.toLocaleString()} matching companies (selection cap reached).`);
      }
    } catch (err) {
      console.error('Select all matching failed:', err);
    } finally {
      setSelectingAll(false);
    }
  };

  // Load waterfall config on mount
  useEffect(() => {
    leadBuilderApi.getWaterfallConfig().then(resp => {
      if (resp.success && resp.data) setWaterfallConfig(resp.data);
    }).catch(() => {});
  }, []);

  // Load contacts and waterfall status when company is selected
  useEffect(() => {
    if (selectedCompany) {
      setCompanyContacts([]);
      setContactDiscoveryResult(null);
      setCompanyWaterfallStatus(null);
      leadBuilderApi.getCompanyMatchContacts(selectedCompany.id).then(resp => {
        if (resp.success && resp.data) {
          setCompanyContacts(resp.data);
        }
      }).catch(() => {});
      leadBuilderApi.getCompanyWaterfallStatus(selectedCompany.id).then(resp => {
        if (resp.success && resp.data) setCompanyWaterfallStatus(resp.data);
      }).catch(() => {});
    }
  }, [selectedCompany?.id]);

  // Handle ICP profile save
  const handleSaveProfile = async (profileData: Partial<ICPProfile>) => {
    setSavingProfile(true);
    try {
      if (editingProfile) {
        await leadBuilderApi.updateICPProfile(editingProfile.id, profileData);
      } else {
        await leadBuilderApi.createICPProfile(profileData);
      }
      setShowICPModal(false);
      setEditingProfile(undefined);
      loadData();
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setSavingProfile(false);
    }
  };

  // Handle profile delete
  const handleDeleteProfile = async (profileId: string) => {
    if (!confirm('Are you sure you want to delete this ICP profile?')) return;
    try {
      await leadBuilderApi.deleteICPProfile(profileId);
      loadData();
    } catch (err) {
      console.error('Error deleting profile:', err);
    }
  };

  // Handle profile duplicate
  const handleDuplicateProfile = async (profileId: string) => {
    try {
      await leadBuilderApi.duplicateICPProfile(profileId);
      loadData();
    } catch (err) {
      console.error('Error duplicating profile:', err);
    }
  };

  // Handle create and start discovery job using the discovery engine
  const handleCreateJob = async (profileId: string, autoStart: boolean = true) => {
    if (!profileId) {
      alert('Please select an ICP profile for the job');
      return;
    }
    setCreatingJob(true);
    try {
      const profile = icpProfiles.find(p => p.id === profileId);

      // Provider ICP profiles use the provider import flow instead of discovery
      if (profile?.profile_type === 'provider') {
        const importRes = await leadBuilderApi.runProviderImport({
          icp_profile_id: profileId,
          max_results: 200,
          discover_contacts: true,
          contact_max_tier: 1,
        });
        if (importRes.success && importRes.data) {
          setShowJobModal(false);
          loadData();
          const d = importRes.data as any;
          const diag = d.diagnostics || {};
          const criteria = diag.icp_criteria || {};
          const contactDiag = diag.contact_discovery || {};
          alert(
            `Provider import complete!\n` +
            `Imported: ${d.providers_imported} | Updated: ${d.providers_updated ?? 0} | Scored: ${d.providers_scored}\n` +
            `Contacts discovered: ${d.contacts_discovered ?? 0} (from ${d.contacts_companies_processed ?? 0} providers)\n\n` +
            `Diagnostics:\n` +
            `  API URL: ${diag.provider_directory_url || 'N/A'}\n` +
            `  Raw from API: ${diag.raw_from_directory_api ?? '?'} | Saved: ${diag.raw_from_saved_providers ?? '?'}\n` +
            `  Unique: ${diag.unique_after_dedup ?? '?'} | Already imported: ${diag.existing_already_imported ?? '?'}\n` +
            (contactDiag.processed ? `  Contact discovery: ${contactDiag.contacts_found} contacts from ${contactDiag.processed} providers\n` : '') +
            `\nICP Criteria:\n` +
            `  Org types: ${JSON.stringify(criteria.resolved_org_types || criteria.target_org_types || [])}\n` +
            `  States: ${JSON.stringify(criteria.target_states || [])}\n` +
            `  Geo: ${criteria.center_lat ? `(${criteria.center_lat},${criteria.center_lon}) r=${criteria.radius}mi` : 'none'}`
          );
        } else {
          throw new Error(importRes.error || 'Failed to import providers');
        }
      } else if (autoStart) {
        // Use the new discovery engine endpoint for auto-start
        const discoveryRes = await leadBuilderApi.startDiscovery({
          icp_profile_id: profileId,
          job_name: `Discovery - ${profile?.name || 'Unknown Profile'}`,
          max_companies: 100,
          max_tier: 1, // Start with free/native sources only
        });

        if (discoveryRes.success && discoveryRes.data) {
          setShowJobModal(false);
          loadData();
          // Show success message
          alert(`Discovery job started! ${discoveryRes.data.message}`);
        } else {
          throw new Error(discoveryRes.error || 'Failed to start discovery');
        }
      } else {
        // Just create the job without starting (old behavior)
        const jobData = {
          name: `Discovery - ${profile?.name || 'Unknown Profile'}`,
          description: `Auto-generated discovery job for ${profile?.name}`,
          icp_profile_id: profileId,
          job_type: 'discovery' as const,
          max_companies: 100,
          max_contacts_per_company: 5,
        };
        const createRes = await leadBuilderApi.createJob(jobData);
        if (createRes.success) {
          setShowJobModal(false);
          loadData();
        }
      }
    } catch (err: any) {
      console.error('Error creating job:', err);
      alert(`Failed: ${err?.message || err?.detail || JSON.stringify(err)}`);
    } finally {
      setCreatingJob(false);
    }
  };

  // Handle enriching existing imported providers with contact discovery waterfall
  const handleEnrichContacts = async (profileId: string) => {
    if (!profileId) return;
    setCreatingJob(true);
    try {
      const enrichRes = await leadBuilderApi.enrichImportedProviders({
        icp_profile_id: profileId,
        max_providers: 50,
        max_tier: 4,
        only_missing_contacts: true,
      });
      if (enrichRes.success && enrichRes.data) {
        loadData();
        const d = enrichRes.data as any;
        const apiKeys = d.api_keys_configured || {};
        const keyStatus = Object.entries(apiKeys)
          .map(([k, v]) => `${k}: ${v ? 'yes' : 'no'}`)
          .join(', ');
        alert(
          `Contact Enrichment Complete!\n\n` +
          `Providers processed: ${d.providers_processed}\n` +
          `Contacts discovered: ${d.contacts_discovered}\n` +
          `IRS-sourced providers: ${d.irs_sourced_count}\n\n` +
          `API Keys: ${keyStatus}\n\n` +
          `${d.message}`
        );
      } else {
        throw new Error(enrichRes.error || 'Failed to enrich providers');
      }
    } catch (err: any) {
      console.error('Error enriching providers:', err);
      alert(`Failed: ${err?.message || err?.detail || JSON.stringify(err)}`);
    } finally {
      setCreatingJob(false);
    }
  };

  const tabs = [
    { id: 'icp-profiles' as const, label: t('platform.leadBuilder.tabs.icpProfiles', 'ICP Profiles'), icon: Target, count: icpProfiles.length },
    { id: 'companies' as const, label: t('platform.leadBuilder.tabs.matchedCompanies', 'Matched Companies'), icon: Building2, count: totalCount || companyMatches.length },
    { id: 'decision-makers' as const, label: t('platform.leadBuilder.tabs.decisionMakers', 'Decision Makers'), icon: UserCheck },
    { id: 'review' as const, label: t('platform.leadBuilder.tabs.review', 'Review'), icon: ClipboardCheck, count: reviewCount },
    { id: 'jobs' as const, label: t('platform.leadBuilder.tabs.discoveryJobs', 'Discovery Jobs'), icon: Zap, count: jobs.length },
    { id: 'outreach-lists' as const, label: t('platform.leadBuilder.tabs.outreachLists', 'Outreach Lists'), icon: Mail },
    { id: 'analytics' as const, label: t('platform.leadBuilder.tabs.analytics', 'Analytics'), icon: BarChart3 },
  ];

  return (
    <div
      className={isTriveny ? 'triveny-platform' : 'min-h-screen bg-gray-50'}
      style={
        isTriveny
          ? {
              minHeight: '100vh',
              background: tvTokens.paper,
              fontFamily: tvTokens.fontSans,
              color: tvTokens.ink,
            }
          : undefined
      }
    >
      {isTriveny ? (
        <>
          <TvPageBar
            eyebrow="Market · ICP Profiles"
            title={t('platform.leadBuilder.title', 'Lead Builder')}
            actions={
              <>
                <TvButton variant="ghost" icon={RefreshCw} onClick={loadData}>
                  Refresh
                </TvButton>
                <TvButton
                  variant="ink"
                  icon={Plus}
                  onClick={() => { setEditingProfile(undefined); setShowICPModal(true); }}
                >
                  {t('platform.leadBuilder.newIcpProfile', 'New ICP Profile')}
                </TvButton>
              </>
            }
          />
          <div
            style={{
              padding: '14px 22px 0',
              borderBottom: `1px solid ${tvTokens.borderMuted}`,
              background: tvTokens.surface,
            }}
          >
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 14px',
                      fontFamily: tvTokens.fontDisplay,
                      fontSize: 13,
                      fontWeight: 600,
                      color: active ? tvTokens.ink : tvTokens.ink500,
                      background: 'transparent',
                      border: 0,
                      borderBottom: `2px solid ${active ? tvTokens.accent : 'transparent'}`,
                      cursor: 'pointer',
                      transition: 'color .12s ease, border-color .12s ease',
                    }}
                    aria-current={active ? 'page' : undefined}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                    {tab.count !== undefined && (
                      <span
                        style={{
                          fontFamily: tvTokens.fontMono,
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: '0.04em',
                          color: active ? tvTokens.accentInk : tvTokens.ink500,
                          background: active ? tvTokens.accentSoft : tvTokens.paper2,
                          borderRadius: 999,
                          padding: '1px 7px',
                        }}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Target className="h-6 w-6 text-blue-600" />{t('platform.leadBuilder.title', 'Lead Builder')}</h1>}
              <p className="text-sm text-gray-500 mt-1">
                {t('platform.leadBuilder.description', 'ICP-based company matching and autonomous lead discovery')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadData}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => { setEditingProfile(undefined); setShowICPModal(true); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('platform.leadBuilder.newIcpProfile', 'New ICP Profile')}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 mt-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`px-1.5 py-0.5 text-xs rounded ${
                    activeTab === tab.id ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-red-600">
            <AlertCircle className="w-12 h-12 mb-4" />
            <p>{error}</p>
            <button onClick={loadData} className="mt-4 px-4 py-2 bg-red-100 rounded-lg hover:bg-red-200">
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* ICP Profiles Tab */}
            {activeTab === 'icp-profiles' && (
              <div>
                {icpProfiles.length === 0 ? (
                  <div className="text-center py-12">
                    <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No ICP Profiles</h3>
                    <p className="text-gray-500 mb-4">Create your first Ideal Customer Profile to start matching companies.</p>
                    <button
                      onClick={() => { setEditingProfile(undefined); setShowICPModal(true); }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Create ICP Profile
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {icpProfiles.map((profile) => (
                      <ICPProfileCard
                        key={profile.id}
                        profile={profile}
                        onEdit={() => { setEditingProfile(profile); setShowICPModal(true); }}
                        onDelete={() => handleDeleteProfile(profile.id)}
                        onDuplicate={() => handleDuplicateProfile(profile.id)}
                        onViewMatches={() => { setSelectedProfileId(profile.id); setActiveTab('companies'); }}
                        onRunDiscovery={() => handleCreateJob(profile.id, true)}
                        onDOLImport={profile.profile_type !== 'provider' ? () => {
                          setSelectedProfileId(profile.id);
                          setShowDOLImportModal(true);
                        } : undefined}
                        onEnrichContacts={profile.profile_type === 'provider' ? () => handleEnrichContacts(profile.id) : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Companies Tab */}
            {activeTab === 'companies' && (
              <div>
                {selectedCompany ? (
                  /* Company Detail View */
                  <div>
                    <button
                      onClick={() => setSelectedCompany(null)}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to list
                    </button>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-7 h-7 text-gray-500" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold text-gray-900">{selectedCompany.company_name}</h2>
                            {selectedCompany.domain && (
                              <a href={`https://${selectedCompany.domain}`} target="_blank" rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                <Globe className="w-3 h-3" />{selectedCompany.domain}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 text-sm font-medium rounded border ${
                            selectedCompany.tier === 1 ? 'bg-green-100 text-green-800 border-green-300' :
                            selectedCompany.tier === 2 ? 'bg-blue-100 text-blue-800 border-blue-300' :
                            selectedCompany.tier === 3 ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                            'bg-orange-100 text-orange-800 border-orange-300'
                          }`}>
                            Tier {selectedCompany.tier}
                          </span>
                          <span className={`px-3 py-1 text-sm rounded ${
                            selectedCompany.status === 'matched' ? 'bg-blue-100 text-blue-700' :
                            selectedCompany.status === 'qualified' ? 'bg-green-100 text-green-700' :
                            selectedCompany.status === 'contacted' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {selectedCompany.status}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Match Score</p>
                          <p className="text-lg font-bold text-gray-900">{(selectedCompany.score * 100).toFixed(0)}%</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Confidence</p>
                          <p className="text-lg font-bold text-gray-900">{(selectedCompany.confidence * 100).toFixed(0)}%</p>
                        </div>
                        {selectedCompany.source_type === 'provider_import' ? (
                          <>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">Quality Score</p>
                              <p className="text-lg font-bold text-gray-900">
                                {selectedCompany.quality_score != null
                                  ? <span className="text-yellow-600">{'★'.repeat(Math.round(Number(selectedCompany.quality_score)))}{'☆'.repeat(5 - Math.round(Number(selectedCompany.quality_score)))}</span>
                                  : '-'}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">Distance</p>
                              <p className="text-lg font-bold text-gray-900">
                                {selectedCompany.distance_miles != null ? `${Number(selectedCompany.distance_miles).toFixed(1)} mi` : '-'}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">Employees</p>
                              <p className="text-lg font-bold text-gray-900">{selectedCompany.employee_count?.toLocaleString() || '-'}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">Self-Insured</p>
                              <p className="text-lg font-bold text-gray-900 flex items-center gap-1">
                                {selectedCompany.is_self_insured ? (
                                  <><CheckCircle className="w-5 h-5 text-green-600" /> Yes</>
                                ) : (
                                  <><XCircle className="w-5 h-5 text-gray-400" /> No</>
                                )}
                              </p>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Provider-specific detail section */}
                      {selectedCompany.source_type === 'provider_import' && (
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
                          <h3 className="text-sm font-semibold text-teal-900 mb-3 flex items-center gap-2">
                            <Heart className="w-4 h-4" /> Provider Directory Info
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                            {selectedCompany.organization_type && (
                              <div>
                                <span className="text-teal-700 text-xs">Organization Type</span>
                                <p className="font-medium text-gray-900">{selectedCompany.organization_type}</p>
                              </div>
                            )}
                            {selectedCompany.provider_services && (selectedCompany.provider_services as string[]).length > 0 && (
                              <div className="col-span-2">
                                <span className="text-teal-700 text-xs">Services</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(selectedCompany.provider_services as string[]).map((svc) => (
                                    <span key={svc} className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">{svc}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {selectedCompany.provider_email && (
                              <div>
                                <span className="text-teal-700 text-xs">Email</span>
                                <p className="font-medium text-gray-900">
                                  <a href={`mailto:${selectedCompany.provider_email}`} className="text-blue-600 hover:underline">
                                    {selectedCompany.provider_email}
                                  </a>
                                </p>
                              </div>
                            )}
                            {selectedCompany.provider_phone && (
                              <div>
                                <span className="text-teal-700 text-xs">Phone</span>
                                <p className="font-medium text-gray-900">{selectedCompany.provider_phone}</p>
                              </div>
                            )}
                            <div className="flex items-center gap-3">
                              {selectedCompany.accepts_medicaid && (
                                <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Medicaid</span>
                              )}
                              {selectedCompany.accepts_medicare && (
                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">Medicare</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Company Info</h3>
                          <dl className="space-y-2">
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">Industry</dt>
                              <dd className="text-sm font-medium text-gray-900">{selectedCompany.industry || '-'}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">Insurance Status</dt>
                              <dd className="text-sm font-medium text-gray-900">{selectedCompany.insurance_status?.replace('_', ' ') || '-'}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">Location</dt>
                              <dd className="text-sm font-medium text-gray-900">
                                {[selectedCompany.headquarters_city, selectedCompany.headquarters_state].filter(Boolean).join(', ') || '-'}
                              </dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">Annual Revenue</dt>
                              <dd className="text-sm font-medium text-gray-900">
                                {selectedCompany.annual_revenue ? `$${selectedCompany.annual_revenue.toLocaleString()}` : '-'}
                              </dd>
                            </div>
                          </dl>
                        </div>

                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Workforce</h3>
                          <dl className="space-y-2">
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">Hourly Workforce</dt>
                              <dd className="text-sm font-medium text-gray-900">{selectedCompany.hourly_workforce_pct ? `${selectedCompany.hourly_workforce_pct}%` : '-'}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">Frontline</dt>
                              <dd className="text-sm font-medium text-gray-900">{selectedCompany.frontline_pct ? `${selectedCompany.frontline_pct}%` : '-'}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">Turnover Rate</dt>
                              <dd className="text-sm font-medium text-gray-900">{selectedCompany.turnover_rate ? `${selectedCompany.turnover_rate}%` : '-'}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">Absenteeism Rate</dt>
                              <dd className="text-sm font-medium text-gray-900">{selectedCompany.absenteeism_rate ? `${selectedCompany.absenteeism_rate}%` : '-'}</dd>
                            </div>
                          </dl>
                        </div>
                      </div>

                      {/* DOL Filing Details (expanded enrichment) */}
                      {selectedCompany.enrichment_data && (
                        <div className="mt-6 border-t pt-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">DOL Filing Details</h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {selectedCompany.enrichment_data.ein && (
                              <div className="bg-gray-50 rounded px-3 py-2">
                                <p className="text-xs text-gray-500">EIN</p>
                                <p className="text-sm font-medium text-gray-900">{selectedCompany.enrichment_data.ein}</p>
                              </div>
                            )}
                            {selectedCompany.enrichment_data.plan_name && (
                              <div className="bg-gray-50 rounded px-3 py-2">
                                <p className="text-xs text-gray-500">Plan Name</p>
                                <p className="text-sm font-medium text-gray-900 truncate">{selectedCompany.enrichment_data.plan_name}</p>
                              </div>
                            )}
                            {selectedCompany.enrichment_data.plan_year && (
                              <div className="bg-gray-50 rounded px-3 py-2">
                                <p className="text-xs text-gray-500">Plan Year</p>
                                <p className="text-sm font-medium text-gray-900">{selectedCompany.enrichment_data.plan_year}</p>
                              </div>
                            )}
                            {selectedCompany.enrichment_data.self_insured_confidence != null && (
                              <div className="bg-gray-50 rounded px-3 py-2">
                                <p className="text-xs text-gray-500">SI Confidence</p>
                                <p className="text-sm font-medium text-gray-900">{(Number(selectedCompany.enrichment_data.self_insured_confidence) * 100).toFixed(0)}%</p>
                              </div>
                            )}
                            {selectedCompany.enrichment_data.active_participant_count && (
                              <div className="bg-gray-50 rounded px-3 py-2">
                                <p className="text-xs text-gray-500">Active Participants</p>
                                <p className="text-sm font-medium text-gray-900">{Number(selectedCompany.enrichment_data.active_participant_count).toLocaleString()}</p>
                              </div>
                            )}
                            {selectedCompany.enrichment_data.total_plan_assets && (
                              <div className="bg-gray-50 rounded px-3 py-2">
                                <p className="text-xs text-gray-500">Plan Assets</p>
                                <p className="text-sm font-medium text-gray-900">${Number(selectedCompany.enrichment_data.total_plan_assets).toLocaleString()}</p>
                              </div>
                            )}
                            {selectedCompany.enrichment_data.benefits_paid && (
                              <div className="bg-gray-50 rounded px-3 py-2">
                                <p className="text-xs text-gray-500">Benefits Paid</p>
                                <p className="text-sm font-medium text-gray-900">${Number(selectedCompany.enrichment_data.benefits_paid).toLocaleString()}</p>
                              </div>
                            )}
                            {selectedCompany.enrichment_data.welfare_benefit_code && (
                              <div className="bg-gray-50 rounded px-3 py-2">
                                <p className="text-xs text-gray-500">Welfare Code</p>
                                <p className="text-sm font-medium text-gray-900">{selectedCompany.enrichment_data.welfare_benefit_code}</p>
                              </div>
                            )}
                            {selectedCompany.enrichment_data.naics_code && (
                              <div className="bg-gray-50 rounded px-3 py-2">
                                <p className="text-xs text-gray-500">NAICS Code</p>
                                <p className="text-sm font-medium text-gray-900">{selectedCompany.enrichment_data.naics_code}</p>
                              </div>
                            )}
                            {selectedCompany.enrichment_data.address_line1 && (
                              <div className="bg-gray-50 rounded px-3 py-2 col-span-2">
                                <p className="text-xs text-gray-500">Address</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {selectedCompany.enrichment_data.address_line1}
                                  {selectedCompany.enrichment_data.address_line2 ? `, ${selectedCompany.enrichment_data.address_line2}` : ''}
                                  {selectedCompany.enrichment_data.zip_code ? ` ${selectedCompany.enrichment_data.zip_code}` : ''}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Admin Contact */}
                          {selectedCompany.enrichment_data.admin_name && (
                            <div className="mt-3 bg-blue-50 rounded-lg p-3">
                              <p className="text-xs font-medium text-blue-700 mb-1">Plan Administrator (from DOL Filing)</p>
                              <div className="flex items-center gap-4">
                                <span className="text-sm font-medium text-gray-900">{selectedCompany.enrichment_data.admin_name}</span>
                                {selectedCompany.enrichment_data.admin_phone && (
                                  <span className="text-sm text-gray-600 flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> {selectedCompany.enrichment_data.admin_phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Contact Discovery Waterfall */}
                      <div className="mt-6 border-t pt-4">
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
                          <Layers className="w-4 h-4 text-blue-600" />
                          Contact Discovery Waterfall
                        </h3>
                        <div className="space-y-2">
                          {(waterfallConfig?.tiers || []).map((tier: any) => {
                            const tierResult = companyWaterfallStatus?.tier_results?.find((tr: any) => tr.tier_number === tier.tier_number);
                            const tierContacts = companyWaterfallStatus?.contacts_by_tier?.[tier.tier_number] || [];
                            const statusColor = tierResult?.status === 'completed'
                              ? 'bg-green-100 border-green-300 text-green-800'
                              : tierResult?.status === 'skipped'
                                ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                                : 'bg-gray-50 border-gray-200 text-gray-500';
                            const iconEl = tierResult?.status === 'completed'
                              ? <CheckCircle className="w-4 h-4 text-green-600" />
                              : tier.contacts_available_from_tier
                                ? <AlertCircle className="w-4 h-4 text-yellow-500" />
                                : <XCircle className="w-4 h-4 text-gray-300" />;

                            return (
                              <div key={tier.tier_number} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${statusColor}`}>
                                <div className="flex items-center gap-2">
                                  {iconEl}
                                  <span className="text-xs font-semibold">Tier {tier.tier_number}</span>
                                  <span className="text-xs">{tier.tier_name}</span>
                                  <span className="text-[10px] text-gray-400">
                                    ({tier.tools.map((t: any) => t.display_name).join(', ')})
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs">
                                  {tierResult?.status === 'completed' && (
                                    <span className="font-medium">{tierResult.contacts_found} contacts ({tierResult.decision_makers_found} DM)</span>
                                  )}
                                  {tierResult?.status === 'not_run' && (
                                    <span className="italic">Not run</span>
                                  )}
                                  {tierResult?.status === 'skipped' && (
                                    <span className="italic">Skipped</span>
                                  )}
                                  {!tierResult && (
                                    <span className="italic">
                                      {tier.is_fully_configured ? 'Ready' : 'Not configured'}
                                    </span>
                                  )}
                                  {tier.tools.filter((t: any) => !t.is_org_configured && t.requires_api_key).map((t: any) => (
                                    <button
                                      key={t.source_name}
                                      onClick={() => {
                                        setProviderToConfig(t.source_name);
                                        setProviderApiKey('');
                                        setProviderClientId(t.client_id || '');
                                        setProviderBudget('50');
                                        setShowProviderConfigModal(true);
                                      }}
                                      className="px-1.5 py-0.5 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded text-[10px] font-medium cursor-pointer"
                                    >
                                      Configure {t.display_name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {waterfallConfig && (
                          <p className="mt-2 text-[10px] text-gray-400">
                            {waterfallConfig.total_configured_tools}/{waterfallConfig.total_available_tools} tools configured
                            {waterfallConfig.total_monthly_budget_usd > 0 && (
                              <> &middot; ${waterfallConfig.total_monthly_usage_usd.toFixed(0)} / ${waterfallConfig.total_monthly_budget_usd.toFixed(0)} monthly budget used</>
                            )}
                          </p>
                        )}
                      </div>

                      {/* Discovered Contacts Section */}
                      <div className="mt-6 border-t pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Discovered Contacts</h3>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={async () => {
                                setIsDiscoveringContacts(true);
                                try {
                                  const resp = await leadBuilderApi.discoverContacts(selectedCompany.id, 4);
                                  if (resp.success && resp.data) {
                                    setContactDiscoveryResult(resp.data);
                                    // Reload contacts
                                    const contactsResp = await leadBuilderApi.getCompanyMatchContacts(selectedCompany.id);
                                    if (contactsResp.success && contactsResp.data) {
                                      setCompanyContacts(contactsResp.data);
                                    }
                                  }
                                } catch (err) {
                                  console.error('Contact discovery failed:', err);
                                } finally {
                                  setIsDiscoveringContacts(false);
                                }
                              }}
                              disabled={isDiscoveringContacts}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg disabled:opacity-50"
                            >
                              {isDiscoveringContacts ? (
                                <><Loader2 className="w-3 h-3 animate-spin" /> Discovering...</>
                              ) : (
                                <><UserPlus className="w-3 h-3" /> Discover Contacts</>
                              )}
                            </button>
                          </div>
                        </div>

                        {contactDiscoveryResult && (
                          <div className="mb-3 p-2 bg-green-50 rounded-lg text-xs text-green-800">
                            Found {contactDiscoveryResult.total_contacts} contacts ({contactDiscoveryResult.decision_makers} decision makers)
                            via {contactDiscoveryResult.sources_used?.join(', ')}
                          </div>
                        )}

                        {companyContacts.length > 0 ? (
                          <div className="space-y-2">
                            {companyContacts.map((contact: DiscoveredContact) => (
                              <div key={contact.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                    contact.is_decision_maker ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                                  }`}>
                                    {contact.first_name?.[0]}{contact.last_name?.[0]}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {contact.first_name} {contact.last_name}
                                      {contact.is_decision_maker && (
                                        <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded">DM</span>
                                      )}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {contact.title || 'Unknown title'}
                                      {contact.department ? ` · ${contact.department}` : ''}
                                    </p>
                                    <ContactProvenanceBadges contact={contact} />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {contact.review_status === 'needs_review' && (
                                    <ContactReviewActions
                                      contactId={contact.id}
                                      onDone={(status) =>
                                        setCompanyContacts((prev) =>
                                          prev.map((c) => (c.id === contact.id ? { ...c, review_status: status } : c)),
                                        )
                                      }
                                    />
                                  )}
                                  {contact.email && (
                                    <a href={`mailto:${contact.email}`} className="p-1 text-gray-400 hover:text-blue-600" title={contact.email}>
                                      <Mail className="w-4 h-4" />
                                    </a>
                                  )}
                                  {contact.phone && (
                                    <a href={`tel:${contact.phone}`} className="p-1 text-gray-400 hover:text-blue-600" title={contact.phone}>
                                      <Phone className="w-4 h-4" />
                                    </a>
                                  )}
                                  {contact.linkedin_url && (
                                    <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-1 text-gray-400 hover:text-blue-600">
                                      <Linkedin className="w-4 h-4" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">No contacts discovered yet. Click "Discover Contacts" to find decision makers.</p>
                        )}
                      </div>

                      {/* Add to Campaign button */}
                      {companyContacts.length > 0 && (
                        <div className="mt-6 border-t pt-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-semibold text-gray-900">Ready for Outreach</h3>
                              <p className="text-xs text-gray-500 mt-0.5">{companyContacts.filter((c: DiscoveredContact) => c.email).length} contacts with email addresses</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setAddToListResult(null);
                                  setShowAddToListModal(true);
                                  contactListsApi.listContactLists({ is_active: true }).then((rows) => {
                                    const opts = (Array.isArray(rows) ? rows : []).map((l: any) => ({ id: l.id, name: l.name, member_count: l.member_count }));
                                    setAvailableLists(opts);
                                    if (opts[0]) setAddToListId(opts[0].id);
                                  }).catch(() => setAvailableLists([]));
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg"
                              >
                                <Users className="w-4 h-4" />
                                Add to List
                              </button>
                              <button
                                onClick={() => setShowAddToCampaignModal(true)}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                              >
                                <Mail className="w-4 h-4" />
                                Add to Campaign
                              </button>
                              <button
                                disabled={runningAutomations}
                                title="Emit a 'Lead Builder match' event for each contact — any matching automation fires (enroll in a campaign / add to a list)."
                                onClick={async () => {
                                  const emailContacts = companyContacts.filter((c: DiscoveredContact) => c.email);
                                  if (!emailContacts.length) return;
                                  setRunningAutomations(true);
                                  let matched = 0;
                                  try {
                                    for (const c of emailContacts.slice(0, 100)) {
                                      const res: any = await marketingApi.ingestAutomationEvent({
                                        event_type: 'lead_builder_match',
                                        email: c.email,
                                        first_name: c.first_name,
                                        last_name: c.last_name,
                                        company: selectedCompany?.company_name,
                                        metadata: { source: 'lead_builder', company_id: selectedCompany?.id },
                                      });
                                      matched += (res?.data?.matched_automations ?? res?.matched_automations ?? 0);
                                    }
                                    alert(matched > 0
                                      ? `Ran automations for ${emailContacts.length} contact(s) — ${matched} automation match(es) fired.`
                                      : `Sent ${emailContacts.length} event(s). No active automation is set to trigger on a Lead Builder match yet — create one in Marketing → Automations.`);
                                  } catch (e: any) {
                                    alert(e?.message || 'Failed to run automations');
                                  } finally {
                                    setRunningAutomations(false);
                                  }
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg disabled:opacity-50"
                              >
                                <Zap className="w-4 h-4" />
                                {runningAutomations ? 'Running…' : 'Run Automations'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Data Sources */}
                      {selectedCompany.data_sources && (Array.isArray(selectedCompany.data_sources) ? selectedCompany.data_sources.length > 0 : false) && (
                        <div className="mt-4 border-t pt-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">Data Sources</h3>
                          <div className="flex flex-wrap gap-2">
                            {(selectedCompany.data_sources as any[]).map((ds: any, i: number) => (
                              <span key={i} className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded">
                                {typeof ds === 'string' ? ds : ds.source || JSON.stringify(ds)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Company List View */
                  <div>
                    {/* Filters */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 space-y-3">
                      {/* Row 1: Search, Profile, Tier */}
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[200px]">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              placeholder="Search companies..."
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                        </div>
                        <select
                          value={selectedProfileId || ''}
                          onChange={(e) => { setSelectedProfileId(e.target.value || null); setCurrentPage(1); }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">All Profiles</option>
                          {icpProfiles.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <select
                          value={tierFilter || ''}
                          onChange={(e) => { setTierFilter(e.target.value ? parseInt(e.target.value) : null); setCurrentPage(1); }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">All Tiers</option>
                          <option value="1">Tier 1 (High)</option>
                          <option value="2">Tier 2 (Medium)</option>
                          <option value="3">Tier 3 (Low)</option>
                        </select>
                        <select
                          value={statusFilter || ''}
                          onChange={(e) => { setStatusFilter(e.target.value || null); setCurrentPage(1); }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">All Statuses</option>
                          <option value="matched">Matched</option>
                          <option value="qualified">Qualified</option>
                          <option value="disqualified">Disqualified</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                        </select>
                      </div>

                      {/* Row 2: Employee count, State, Plan type, Self-insured */}
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500 whitespace-nowrap">Employees:</span>
                          <input
                            type="number"
                            value={minEmployees ?? ''}
                            onChange={(e) => { setMinEmployees(e.target.value ? parseInt(e.target.value) : null); setCurrentPage(1); }}
                            placeholder="Min"
                            className="w-20 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                          <span className="text-xs text-gray-400">-</span>
                          <input
                            type="number"
                            value={maxEmployees ?? ''}
                            onChange={(e) => { setMaxEmployees(e.target.value ? parseInt(e.target.value) : null); setCurrentPage(1); }}
                            placeholder="Max"
                            className="w-20 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                        <select
                          value={stateFilter || ''}
                          onChange={(e) => { setStateFilter(e.target.value || null); setCurrentPage(1); }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">All States</option>
                          {['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(st => (
                            <option key={st} value={st}>{st}</option>
                          ))}
                        </select>
                        <select
                          value={planTypeFilter || ''}
                          onChange={(e) => { setPlanTypeFilter(e.target.value || null); setCurrentPage(1); }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">All Plan Types</option>
                          <option value="health">Health/Welfare Plans</option>
                          <option value="pension">Pension Plans Only</option>
                          <option value="4A">FSA (4A)</option>
                          <option value="4B">Medical (4B)</option>
                          <option value="4C">Dental (4C)</option>
                          <option value="4D">Vision (4D)</option>
                          <option value="4H">Stop-Loss (4H)</option>
                        </select>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selfInsuredOnly}
                            onChange={(e) => { setSelfInsuredOnly(e.target.checked); setCurrentPage(1); }}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 whitespace-nowrap">Self-Insured Only</span>
                        </label>
                        {(minEmployees || maxEmployees || stateFilter || planTypeFilter || selfInsuredOnly || searchQuery || statusFilter || stageFilter) && (
                          <button
                            onClick={() => {
                              setMinEmployees(null); setMaxEmployees(null); setStateFilter(null);
                              setPlanTypeFilter(null); setSelfInsuredOnly(false); setSearchQuery('');
                              setStatusFilter(null); setStageFilter(null); setCurrentPage(1);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                          >
                            Clear Filters
                          </button>
                        )}
                      </div>

                      {/* Enrichment / outreach-readiness funnel — click a stage to filter, then
                          "Select all matching" + Create Outreach List to campaign that cohort */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {([
                          { key: null, label: 'All', count: stageCounts?.total, cls: 'bg-gray-100 text-gray-700 border-gray-300', active: 'bg-gray-800 text-white border-gray-800' },
                          { key: 'matched', label: 'Matched', count: stageCounts?.matched, cls: 'bg-gray-50 text-gray-600 border-gray-200', active: 'bg-gray-600 text-white border-gray-600' },
                          { key: 'enriched', label: 'Domain enriched', count: stageCounts?.enriched, cls: 'bg-blue-50 text-blue-700 border-blue-200', active: 'bg-blue-600 text-white border-blue-600' },
                          { key: 'contacts_discovered', label: 'Contacts found', count: stageCounts?.contacts_discovered, cls: 'bg-amber-50 text-amber-700 border-amber-200', active: 'bg-amber-600 text-white border-amber-600' },
                          { key: 'outreach_ready', label: 'Outreach-ready', count: stageCounts?.outreach_ready, cls: 'bg-green-50 text-green-700 border-green-200', active: 'bg-green-600 text-white border-green-600' },
                        ] as Array<{ key: string | null; label: string; count?: number; cls: string; active: string }>).map((chip) => {
                          const isActive = stageFilter === chip.key;
                          return (
                            <button
                              key={chip.label}
                              onClick={() => { setStageFilter(chip.key); setCurrentPage(1); }}
                              className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${isActive ? chip.active : chip.cls}`}
                              title={chip.key === 'outreach_ready' ? 'Has at least one contact with an email — ready to campaign' : chip.key === 'contacts_discovered' ? 'Contacts discovered but none with an email yet' : chip.key === 'enriched' ? 'Domain resolved; run Discover Contacts next' : chip.key === 'matched' ? 'Imported; needs domain enrichment' : 'All matched companies'}
                            >
                              {chip.label}
                              {chip.count !== undefined && <span className="ml-1 opacity-80">{chip.count.toLocaleString()}</span>}
                            </button>
                          );
                        })}
                      </div>

                      {/* Result count + Actions */}
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                          Showing {companyMatches.length} of {totalCount.toLocaleString()} companies
                          {(minEmployees || maxEmployees || stateFilter || planTypeFilter || selfInsuredOnly || tierFilter || debouncedSearch) && ' (filtered)'}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              setIsBackfillingDomains(true);
                              try {
                                const resp = await leadBuilderApi.backfillDomains({ limit: 500 });
                                if (resp.success && resp.data) {
                                  const c = resp.data.counts;
                                  alert(
                                    `Domain backfill: resolved ${resp.data.resolved} of ${resp.data.considered}\n` +
                                    `• provider enrichment: ${c.from_enrichment}\n` +
                                    `• provider directory API: ${c.from_provider_api}\n` +
                                    `• Brave search: ${c.from_brave}${resp.data.brave_enabled ? '' : ' (disabled — no key)'}\n` +
                                    `• still unresolved: ${c.unresolved}`
                                  );
                                  loadData();
                                } else {
                                  alert(`Error: ${resp.error || 'Domain backfill failed'}`);
                                }
                              } catch (err) {
                                console.error('Domain backfill error:', err);
                                alert('Domain backfill failed');
                              } finally {
                                setIsBackfillingDomains(false);
                              }
                            }}
                            disabled={isBackfillingDomains}
                            title="Fill in missing company domains from provider enrichment, the provider directory, then Brave search"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg border border-teal-200 disabled:opacity-50"
                          >
                            {isBackfillingDomains ? (
                              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Backfilling...</>
                            ) : (
                              <><Globe className="w-3.5 h-3.5" /> Backfill Domains</>
                            )}
                          </button>
                          <button
                            onClick={async () => {
                              setIsBulkDiscovering(true);
                              setBulkDiscoveryResult(null);
                              try {
                                const resp = await leadBuilderApi.bulkDiscoverContacts({
                                  max_companies: 500,
                                  max_tier: 4,
                                  tier_filter: tierFilter || undefined,
                                });
                                if (resp.success && resp.data) {
                                  const n = resp.data.companies_total ?? 0;
                                  if (n === 0) {
                                    alert('No companies found needing contact discovery.');
                                  } else {
                                    alert(`Contact discovery started for ${n} companies (top by score, missing contacts). Track progress in the Jobs tab.`);
                                    setActiveTab('jobs');
                                  }
                                  loadData();
                                } else {
                                  alert(`Error: ${resp.error || 'Bulk discovery failed'}`);
                                }
                              } catch (err) {
                                console.error('Bulk discovery error:', err);
                                alert('Bulk contact discovery failed');
                              } finally {
                                setIsBulkDiscovering(false);
                              }
                            }}
                            disabled={isBulkDiscovering}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 disabled:opacity-50"
                          >
                            {isBulkDiscovering ? (
                              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Discovering...</>
                            ) : (
                              <><UserPlus className="w-3.5 h-3.5" /> Discover Contacts (Bulk)</>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setShowCreateListModal(true);
                              setCreateListName('');
                              setCreateListDMOnly(false);
                              setCreateListMinConfidence(0);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            Create Outreach List
                          </button>
                          <button
                            onClick={() => {
                              setAssignIcpTarget('');
                              setShowAssignIcpModal(true);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200"
                          >
                            <Target className="w-3.5 h-3.5" />
                            Assign All Unassigned
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await leadBuilderApi.exportCompanyMatchesCsv({
                                  icp_profile_id: selectedProfileId || undefined,
                                  status: statusFilter || undefined,
                                  tier: tierFilter || undefined,
                                  min_employees: minEmployees || undefined,
                                  max_employees: maxEmployees || undefined,
                                  state: stateFilter || undefined,
                                  is_self_insured: selfInsuredOnly ? true : undefined,
                                  plan_type: planTypeFilter || undefined,
                                  search: debouncedSearch || undefined,
                                });
                              } catch (err) {
                                console.error('CSV export failed:', err);
                                alert('Failed to export CSV');
                              }
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download CSV
                          </button>
                        </div>
                      </div>

                      {/* Bulk discovery result banner */}
                      {bulkDiscoveryResult && (
                        <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800 flex items-center justify-between">
                          <span>
                            Discovered {bulkDiscoveryResult.total_contacts} contacts ({bulkDiscoveryResult.total_decision_makers} decision makers)
                            across {bulkDiscoveryResult.companies_processed} companies
                          </span>
                          <button onClick={() => setBulkDiscoveryResult(null)} className="text-green-600 hover:text-green-800">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Contact Discovery Waterfall Overview */}
                    {waterfallConfig && (
                      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
                            <Layers className="w-3.5 h-3.5 text-blue-600" />
                            Contact Discovery Waterfall
                          </h3>
                          <span className="text-[10px] text-gray-400">
                            {waterfallConfig.total_configured_tools}/{waterfallConfig.total_available_tools} tools configured
                            {waterfallConfig.total_monthly_budget_usd > 0 && (
                              <> &middot; ${waterfallConfig.total_monthly_usage_usd.toFixed(0)} / ${waterfallConfig.total_monthly_budget_usd.toFixed(0)} budget</>
                            )}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {waterfallConfig.tiers.map((tier: any) => (
                            <div
                              key={tier.tier_number}
                              className={`rounded-lg border p-2.5 text-center ${
                                tier.is_fully_configured
                                  ? 'bg-green-50 border-green-200'
                                  : tier.contacts_available_from_tier
                                    ? 'bg-yellow-50 border-yellow-200'
                                    : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-center justify-center gap-1 mb-1">
                                {tier.is_fully_configured
                                  ? <CheckCircle className="w-3 h-3 text-green-600" />
                                  : tier.contacts_available_from_tier
                                    ? <AlertCircle className="w-3 h-3 text-yellow-500" />
                                    : <XCircle className="w-3 h-3 text-gray-300" />}
                                <span className="text-[10px] font-bold text-gray-600">Tier {tier.tier_number}</span>
                              </div>
                              <p className="text-[10px] font-medium text-gray-700 leading-tight">{tier.tier_name}</p>
                              <div className="mt-1.5 space-y-0.5">
                                {tier.tools.map((t: any) => {
                                  // green only when actually verified (or no key needed);
                                  // amber = key saved but not verified/failing; red = needs a key, none set.
                                  const dot = (!t.requires_api_key || t.is_verified)
                                    ? 'bg-green-500'
                                    : t.has_key
                                      ? 'bg-amber-400'
                                      : 'bg-red-400';
                                  return (
                                  <div key={t.source_name} className="flex items-center justify-center gap-1">
                                    <span className={`w-1.5 h-1.5 rounded-full ${dot}`}
                                      title={t.verification_error || (t.requires_api_key ? (t.is_verified ? 'Verified' : t.has_key ? 'Key saved — not verified' : 'No API key') : '')} />
                                    <span className="text-[9px] text-gray-500">{t.display_name}</span>
                                    {t.requires_api_key && (
                                      <button
                                        onClick={() => {
                                          setProviderToConfig(t.source_name);
                                          setProviderApiKey('');
                                          setProviderClientId(t.client_id || '');
                                          setProviderBudget('50');
                                          setShowProviderConfigModal(true);
                                        }}
                                        className="text-[8px] text-orange-600 hover:text-orange-800 underline"
                                      >
                                        {t.has_key ? 'manage' : 'setup'}
                                      </button>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bulk Action Bar */}
                    {selectedCompanyIds.size > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-blue-800">
                            {selectedCompanyIds.size} {selectedCompanyIds.size === 1 ? 'company' : 'companies'} selected
                          </span>
                          {totalCount > selectedCompanyIds.size && (
                            <button
                              onClick={selectAllMatching}
                              disabled={selectingAll}
                              className="text-xs font-medium text-blue-700 underline hover:text-blue-900 disabled:opacity-50"
                            >
                              {selectingAll ? 'Selecting…' : `Select all ${totalCount.toLocaleString()} matching filter`}
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={assignIcpTarget}
                            onChange={(e) => setAssignIcpTarget(e.target.value)}
                            className="text-xs border border-blue-300 rounded px-2 py-1.5 bg-white"
                          >
                            <option value="">Assign to ICP profile...</option>
                            {(icpProfiles || []).map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                          <button
                            disabled={!assignIcpTarget || assigningIcp}
                            onClick={async () => {
                              if (!assignIcpTarget) return;
                              setAssigningIcp(true);
                              try {
                                const resp = await leadBuilderApi.assignIcpToMatches({
                                  icp_profile_id: assignIcpTarget,
                                  match_ids: Array.from(selectedCompanyIds),
                                });
                                if (resp.success && resp.data) {
                                  alert(`Assigned ${resp.data.matches_assigned} companies to "${resp.data.icp_profile_name}"`);
                                  setSelectedCompanyIds(new Set());
                                  setAssignIcpTarget('');
                                  loadData();
                                } else {
                                  alert(`Error: ${resp.error || 'Assignment failed'}`);
                                }
                              } catch (err) {
                                console.error('Assign ICP error:', err);
                                alert('Failed to assign ICP profile');
                              } finally {
                                setAssigningIcp(false);
                              }
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                          >
                            {assigningIcp ? <Loader2 className="w-3 h-3 animate-spin" /> : <Target className="w-3 h-3" />}
                            Assign
                          </button>
                          <button
                            onClick={async () => {
                              const ids = Array.from(selectedCompanyIds);
                              try {
                                const resp = await leadBuilderApi.bulkDiscoverContacts({ match_ids: ids, max_tier: 4 });
                                if (resp.success && resp.data) {
                                  alert(`Contact discovery started for ${resp.data.companies_total ?? ids.length} companies. Track progress in the Jobs tab.`);
                                  setSelectedCompanyIds(new Set());
                                  setActiveTab('jobs');
                                  loadData();
                                } else {
                                  alert(`Error: ${resp.error || 'Failed to start contact discovery'}`);
                                }
                              } catch (err) {
                                console.error('Bulk discover error:', err);
                                alert('Failed to start contact discovery');
                              }
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded"
                          >
                            <UserPlus className="w-3 h-3" /> Discover Contacts
                          </button>
                          <button
                            onClick={() => {
                              setShowCreateListModal(true);
                              setCreateListName('');
                              setCreateListDMOnly(false);
                              setCreateListMinConfidence(0);
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded"
                          >
                            <Mail className="w-3 h-3" /> Move to Cold Outreach
                          </button>
                          <button
                            onClick={() => setSelectedCompanyIds(new Set())}
                            className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1.5"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Company Table */}
                    {(() => {
                      // Search is now server-side via debouncedSearch param
                      const displayed = companyMatches;
                      const effectiveTotal = totalCount;
                      const totalPages = Math.ceil(totalCount / pageSize);

                      return (
                        <>
                          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                  <th className="px-2 py-3 w-8">
                                    <input
                                      type="checkbox"
                                      checked={displayed.length > 0 && displayed.every(m => selectedCompanyIds.has(m.id))}
                                      onChange={(e) => {
                                        const newSet = new Set(selectedCompanyIds);
                                        displayed.forEach(m => e.target.checked ? newSet.add(m.id) : newSet.delete(m.id));
                                        setSelectedCompanyIds(newSet);
                                      }}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('company_name')}>Company{sortBy === 'company_name' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('tier')}>Tier{sortBy === 'tier' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('score')}>Score{sortBy === 'score' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Industry</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('employee_count')}>Employees{sortBy === 'employee_count' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Self-Insured</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {displayed.length === 0 ? (
                                  <tr>
                                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                                      {totalCount === 0
                                        ? 'No matched companies yet. Run a discovery job or import DOL data.'
                                        : 'No companies match your search.'}
                                    </td>
                                  </tr>
                                ) : (
                                  displayed.map((match) => (
                                    <CompanyMatchRow
                                      key={match.id}
                                      match={match}
                                      onView={() => setSelectedCompany(match)}
                                      isSelected={selectedCompanyIds.has(match.id)}
                                      onSelectChange={(id, selected) => {
                                        const newSet = new Set(selectedCompanyIds);
                                        selected ? newSet.add(id) : newSet.delete(id);
                                        setSelectedCompanyIds(newSet);
                                      }}
                                    />
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Pagination */}
                          {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-4">
                              <p className="text-sm text-gray-600">
                                Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, effectiveTotal)} of {effectiveTotal} companies
                              </p>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                  disabled={currentPage === 1}
                                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Previous
                                </button>
                                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                  let pageNum: number;
                                  if (totalPages <= 7) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 4) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 3) {
                                    pageNum = totalPages - 6 + i;
                                  } else {
                                    pageNum = currentPage - 3 + i;
                                  }
                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => setCurrentPage(pageNum)}
                                      className={`px-3 py-1.5 text-sm rounded-lg ${
                                        currentPage === pageNum
                                          ? 'bg-blue-600 text-white'
                                          : 'border border-gray-300 hover:bg-gray-50'
                                      }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                                <button
                                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                  disabled={currentPage === totalPages}
                                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Jobs Tab */}
            {activeTab === 'jobs' && (
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Discovery Jobs</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowDOLImportModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Import DOL Data
                    </button>
                    <button
                      onClick={() => setShowJobModal(true)}
                      disabled={icpProfiles.length === 0}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Play className="w-4 h-4" />
                      Start New Job
                    </button>
                  </div>
                </div>
                {jobs.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Zap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Discovery Jobs</h3>
                    <p className="text-gray-500">Start a discovery job to find companies matching your ICP profiles.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {jobs.map((job) => (
                      <div key={job.id} className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              job.status === 'running' ? 'bg-green-100' :
                              job.status === 'completed' ? 'bg-blue-100' :
                              job.status === 'failed' ? 'bg-red-100' : 'bg-gray-100'
                            }`}>
                              <Zap className={`w-5 h-5 ${
                                job.status === 'running' ? 'text-green-600' :
                                job.status === 'completed' ? 'text-blue-600' :
                                job.status === 'failed' ? 'text-red-600' : 'text-gray-600'
                              }`} />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{job.name || `Job ${job.id.slice(0, 8)}`}</h3>
                              <p className="text-sm text-gray-500 flex items-center gap-1">
                                {job.job_type} - {job.status}
                                {(job.status === 'running' || job.status === 'queued') && <Loader2 className="w-3 h-3 animate-spin" />}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">{job.companies_matched}</span>
                              {(job as any).companies_found ? `/${(job as any).companies_found}` : ''} companies
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">{job.contacts_discovered}</span> contacts found
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Outreach Lists Tab */}
            {activeTab === 'outreach-lists' && (
              <OutreachListsTab onGoToCompanies={() => setActiveTab('companies')} />
            )}

            {/* Decision Makers Tab (TRUS-770) */}
            {activeTab === 'decision-makers' && <DecisionMakersTab onOpenJobs={() => setActiveTab('jobs')} />}

            {/* Review Tab (TRUS-770) */}
            {activeTab === 'review' && <ContactReviewTab onCountChange={setReviewCount} />}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div>
                {analytics ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Target className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Active Profiles</p>
                          <p className="text-2xl font-bold text-gray-900">{analytics.active_profiles}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Companies Matched</p>
                          <p className="text-2xl font-bold text-gray-900">{analytics.total_companies_matched}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Contacts Discovered</p>
                          <p className="text-2xl font-bold text-gray-900">{analytics.total_contacts_discovered}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Zap className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Active Jobs</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {analytics.jobs_by_status?.running || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No analytics data available
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ICP Profile Modal */}
      <ICPProfileFormModal
        isOpen={showICPModal}
        onClose={() => { setShowICPModal(false); setEditingProfile(undefined); }}
        onSubmit={handleSaveProfile}
        profile={editingProfile}
        isLoading={savingProfile}
      />

      {/* DOL 5500 Import Modal */}
      <Dialog open={showDOLImportModal} onOpenChange={() => setShowDOLImportModal(false)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import DOL Form 5500 Data</DialogTitle>
            <DialogDescription>
              Upload a local EFAST2 CSV file or browse DOL FOIA bulk datasets to import employer data for lead discovery.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <DOL5500ImportWizard
              targetTable="lb_company_matches"
              icpProfiles={(icpProfiles || []).map(p => ({ id: p.id, name: p.name, profile_type: p.profile_type }))}
              defaultIcpProfileId={selectedProfileId || null}
              onComplete={() => {
                setShowDOLImportModal(false);
                loadData();
              }}
              onCancel={() => setShowDOLImportModal(false)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign to ICP Modal */}
      <Dialog open={showAssignIcpModal} onOpenChange={() => setShowAssignIcpModal(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Unassigned Companies to ICP Profile</DialogTitle>
            <DialogDescription>
              Link companies that have no ICP profile (e.g. DOL imports) to an employer ICP profile for scoring and bulk contact discovery.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select ICP Profile</label>
              <select
                value={assignIcpTarget}
                onChange={(e) => setAssignIcpTarget(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 pl-3 pr-10 text-sm"
              >
                <option value="">-- Select a profile --</option>
                {(icpProfiles || [])
                  .filter(p => !p.profile_type || p.profile_type === 'employer')
                  .map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
              </select>
            </div>
            <p className="text-xs text-gray-500">
              This will assign all unassigned companies to the selected ICP profile. Companies already linked to a profile will not be affected.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowAssignIcpModal(false)}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              disabled={!assignIcpTarget || assigningIcp}
              onClick={async () => {
                setAssigningIcp(true);
                try {
                  const resp = await leadBuilderApi.assignIcpToMatches({
                    icp_profile_id: assignIcpTarget,
                    filters: { source_type: 'dol_5500' },
                  });
                  if (resp.success && resp.data) {
                    alert(`Assigned ${resp.data.matches_assigned} companies to "${resp.data.icp_profile_name}"`);
                    setShowAssignIcpModal(false);
                    loadData();
                  } else {
                    alert(`Error: ${resp.error || 'Assignment failed'}`);
                  }
                } catch (err) {
                  console.error('Assign ICP error:', err);
                  alert('Failed to assign ICP profile');
                } finally {
                  setAssigningIcp(false);
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
            >
              {assigningIcp ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Assigning...</>
              ) : (
                <><Target className="w-4 h-4" /> Assign to ICP</>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Creation Modal */}
      <Dialog open={showJobModal} onOpenChange={() => setShowJobModal(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start Discovery Job</DialogTitle>
            <DialogDescription>
              Select an ICP profile to run discovery or import providers from your directory.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select ICP Profile</label>
            <select
              id="job-profile-select"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              defaultValue={icpProfiles.find(p => p.is_default)?.id || icpProfiles[0]?.id || ''}
            >
              {icpProfiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name} {profile.is_default && '(Default)'} {profile.profile_type === 'provider' && '[Provider]'}
                </option>
              ))}
            </select>
            <p className="mt-2 text-sm text-gray-500">
              {icpProfiles.find(p => p.id === (document.getElementById('job-profile-select') as HTMLSelectElement)?.value)?.profile_type === 'provider'
                ? 'This will import matching providers from your directory.'
                : 'The job will search for companies matching the selected ICP criteria.'}
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowJobModal(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const select = document.getElementById('job-profile-select') as HTMLSelectElement;
                handleCreateJob(select?.value || '', true);
              }}
              disabled={creatingJob || icpProfiles.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
            >
              {creatingJob && <Loader2 className="w-4 h-4 animate-spin" />}
              Start Job
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Campaign Modal */}
      <Dialog open={showAddToCampaignModal} onOpenChange={(open) => {
        setShowAddToCampaignModal(open);
        if (open) {
          setCampaignMode('existing');
          setCampaignId('');
          setNewCampaignName('');
          leadBuilderApi.getAvailableCampaigns().then(resp => {
            if (resp.success && resp.data) setAvailableCampaigns(resp.data);
          }).catch(() => {});
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Contacts to Outreach Campaign</DialogTitle>
            <DialogDescription>
              Add {companyContacts.filter((c: DiscoveredContact) => c.email).length} contacts from {selectedCompany?.company_name} to a cold outreach campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Campaign selection mode */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setCampaignMode('existing')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  campaignMode === 'existing' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Existing Campaign
              </button>
              <button
                onClick={() => setCampaignMode('new')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  campaignMode === 'new' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Create New Campaign
              </button>
            </div>

            {campaignMode === 'existing' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Campaign</label>
                {availableCampaigns.length > 0 ? (
                  <select
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">Select a campaign...</option>
                    {availableCampaigns.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.status}) - {c.total_prospects} prospects
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-500 italic">No campaigns available. Create a new one below.</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
                  <input
                    type="text"
                    value={newCampaignName}
                    onChange={(e) => setNewCampaignName(e.target.value)}
                    placeholder={`Outreach - ${selectedCompany?.company_name || 'Company'}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Type</label>
                  <select
                    value={newCampaignType}
                    onChange={(e) => setNewCampaignType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="cold_email">Cold Email</option>
                    <option value="cold_linkedin">Cold LinkedIn</option>
                    <option value="cold_phone">Cold Phone</option>
                    <option value="multi_channel">Multi-Channel</option>
                  </select>
                </div>
              </div>
            )}

            {/* Contacts preview */}
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Contacts to add:</h4>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {companyContacts.filter((c: DiscoveredContact) => c.email).map((c: DiscoveredContact) => (
                  <div key={c.id} className="text-xs text-gray-600 flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                    {c.first_name} {c.last_name} — {c.email}
                    {c.is_decision_maker && <span className="text-[10px] bg-green-100 text-green-700 px-1 rounded">DM</span>}
                  </div>
                ))}
                {companyContacts.filter((c: DiscoveredContact) => !c.email).map((c: DiscoveredContact) => (
                  <div key={c.id} className="text-xs text-gray-400 flex items-center gap-2">
                    <XCircle className="w-3 h-3 text-gray-300 flex-shrink-0" />
                    {c.first_name} {c.last_name} — no email (skipped)
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowAddToCampaignModal(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setAddingToCampaign(true);
                try {
                  const contactIds = companyContacts
                    .filter((c: DiscoveredContact) => c.email)
                    .map((c: DiscoveredContact) => c.id);

                  if (campaignMode === 'new') {
                    const name = newCampaignName.trim() || `Outreach - ${selectedCompany?.company_name || 'Company'}`;
                    const resp = await leadBuilderApi.createOutreachList({
                      campaign_name: name,
                      campaign_type: newCampaignType,
                      contact_ids: contactIds,
                    });
                    if (resp.success && resp.data) {
                      setShowAddToCampaignModal(false);
                      setNewCampaignName('');
                      alert(`Campaign "${name}" created with ${resp.data.prospects_added} prospects!`);
                    } else {
                      alert(`Error: ${resp.error || 'Failed to create campaign'}`);
                    }
                  } else {
                    if (!campaignId) return;
                    const resp = await leadBuilderApi.addContactsToCampaign(campaignId, contactIds);
                    if (resp.success) {
                      setShowAddToCampaignModal(false);
                      setCampaignId('');
                      alert(`Successfully added ${resp.data?.added || contactIds.length} contacts to campaign!`);
                    } else {
                      alert(`Error: ${resp.error || 'Failed to add contacts'}`);
                    }
                  }
                } catch (err) {
                  console.error('Error adding to campaign:', err);
                  alert('Failed to add contacts to campaign');
                } finally {
                  setAddingToCampaign(false);
                }
              }}
              disabled={
                addingToCampaign ||
                companyContacts.filter((c: DiscoveredContact) => c.email).length === 0 ||
                (campaignMode === 'existing' && !campaignId)
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
            >
              {addingToCampaign && <Loader2 className="w-4 h-4 animate-spin" />}
              <Mail className="w-4 h-4" />
              {campaignMode === 'new' ? 'Create Campaign' : `Add ${companyContacts.filter((c: DiscoveredContact) => c.email).length} Contacts`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add discovered contacts to a nurture contact list (spine #1) */}
      <Dialog open={showAddToListModal} onOpenChange={setShowAddToListModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add to a contact list</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              Add the {companyContacts.filter((c: DiscoveredContact) => c.email).length} contact(s) with an email from {selectedCompany?.company_name} to a contact list — ready to target with a nurture campaign.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Existing list</label>
              <select
                value={addToListId}
                onChange={(e) => { setAddToListId(e.target.value); setNewListName(''); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Select a list…</option>
                {availableLists.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}{l.member_count != null ? ` (${l.member_count})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="text-center text-xs text-gray-400">or</div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Create a new list</label>
              <input
                value={newListName}
                onChange={(e) => { setNewListName(e.target.value); if (e.target.value) setAddToListId(''); }}
                placeholder="New list name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            {addToListResult && <p className={`text-sm ${addToListResult.startsWith('Added') ? 'text-green-600' : 'text-red-600'}`}>{addToListResult}</p>}
          </div>
          <DialogFooter>
            <button onClick={() => setShowAddToListModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
            <button
              disabled={addingToList || (!addToListId && !newListName.trim())}
              onClick={async () => {
                setAddingToList(true);
                setAddToListResult(null);
                try {
                  let listId = addToListId;
                  if (!listId && newListName.trim()) {
                    const created: any = await contactListsApi.createContactList({ name: newListName.trim(), list_type: 'static' } as any);
                    listId = created?.id;
                  }
                  if (!listId) { setAddToListResult('Pick or name a list first'); setAddingToList(false); return; }
                  const contacts = companyContacts
                    .filter((c: DiscoveredContact) => c.email)
                    .map((c: DiscoveredContact) => ({ email: c.email, first_name: c.first_name, last_name: c.last_name, company: selectedCompany?.company_name }));
                  const res = await contactListsApi.importContacts(listId, contacts, 'lead_builder');
                  setAddToListResult(`Added ${res.added} contact(s)${res.skipped ? `, ${res.skipped} already there` : ''}.`);
                } catch (e: any) {
                  setAddToListResult(e?.message || 'Failed to add to list');
                } finally {
                  setAddingToList(false);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
            >
              {addingToList ? 'Adding…' : 'Add to list'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provider Configuration Modal */}
      <Dialog open={showProviderConfigModal} onOpenChange={setShowProviderConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configure {
              {hunter_io: 'Hunter.io', apollo_io: 'Apollo.io', clearbit: 'Clearbit', zoominfo: 'ZoomInfo'}[providerToConfig] || providerToConfig
            }</DialogTitle>
            <DialogDescription>
              Enter your API key to enable this contact discovery tool.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {(() => {
              const cur = (waterfallConfig as any)?.tiers?.flatMap((tt: any) => tt.tools || []).find((x: any) => x.source_name === providerToConfig);
              if (!cur) return null;
              if (cur.is_verified) return <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">✓ Key verified and working.</div>;
              if (cur.has_key) return <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">⚠ A key is saved but not verified{cur.verification_error ? `: ${cur.verification_error}` : '. Click Verify to test it.'}</div>;
              return null;
            })()}
            {providerToConfig === 'zoominfo' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username (Login Email)</label>
                <input
                  type="text"
                  value={providerClientId}
                  onChange={(e) => setProviderClientId(e.target.value)}
                  placeholder="ZoomInfo login email (e.g. you@trust.care)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">ZoomInfo authenticates with your account login email plus its password. Use the same credentials you sign in to zoominfo.com with (username/password API access must be enabled — not SSO).</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {providerToConfig === 'zoominfo' ? 'Password' : 'API Key'}
              </label>
              <input
                type="password"
                value={providerApiKey}
                onChange={(e) => setProviderApiKey(e.target.value)}
                placeholder="Enter API key (replaces any existing key)…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Budget (USD)</label>
              <input
                type="number"
                value={providerBudget}
                onChange={(e) => setProviderBudget(e.target.value)}
                placeholder="50"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Set a monthly spending limit for this provider.</p>
            </div>
          </div>
          <DialogFooter className="flex items-center gap-2">
            {(() => {
              const cur = (waterfallConfig as any)?.tiers?.flatMap((tt: any) => tt.tools || []).find((x: any) => x.source_name === providerToConfig);
              return cur?.has_key ? (
                <button
                  onClick={async () => {
                    if (!confirm(`Remove the ${providerToConfig} API key?`)) return;
                    try {
                      await leadBuilderApi.deleteProvider(providerToConfig);
                      const r = await leadBuilderApi.getWaterfallConfig();
                      if (r.success && r.data) setWaterfallConfig(r.data);
                      setShowProviderConfigModal(false);
                    } catch (e) { console.error(e); alert('Failed to remove provider'); }
                  }}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm mr-auto"
                >
                  Remove
                </button>
              ) : <span className="mr-auto" />;
            })()}
            <button
              onClick={() => setShowProviderConfigModal(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            {(() => {
              const cur = (waterfallConfig as any)?.tiers?.flatMap((tt: any) => tt.tools || []).find((x: any) => x.source_name === providerToConfig);
              return cur?.has_key ? (
                <button
                  onClick={async () => {
                    setSavingProvider(true);
                    try {
                      const v = await leadBuilderApi.verifyProvider(providerToConfig);
                      const r = await leadBuilderApi.getWaterfallConfig();
                      if (r.success && r.data) setWaterfallConfig(r.data);
                      alert(v.data?.is_verified ? `${providerToConfig} verified ✓` : `Verification failed: ${v.data?.error || v.data?.message || 'invalid key'}`);
                    } catch (e) { console.error(e); alert('Verify failed'); }
                    finally { setSavingProvider(false); }
                  }}
                  disabled={savingProvider}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
                >
                  Verify
                </button>
              ) : null;
            })()}
            <button
              onClick={async () => {
                if (!providerApiKey.trim()) return;
                if (providerToConfig === 'zoominfo' && !providerClientId.trim()) {
                  alert('ZoomInfo requires a Client ID (username) in addition to the API key.');
                  return;
                }
                setSavingProvider(true);
                try {
                  const resp = await leadBuilderApi.configureProvider(providerToConfig, {
                    api_key: providerApiKey.trim(),
                    monthly_budget_usd: parseFloat(providerBudget) || 50,
                    ...(providerToConfig === 'zoominfo'
                      ? { config: { client_id: providerClientId.trim() } }
                      : {}),
                  });
                  if (resp.success) {
                    // Immediately verify the new key so the status reflects reality
                    const v = await leadBuilderApi.verifyProvider(providerToConfig);
                    const r = await leadBuilderApi.getWaterfallConfig();
                    if (r.success && r.data) setWaterfallConfig(r.data);
                    setShowProviderConfigModal(false);
                    alert(v.data?.is_verified ? `${providerToConfig} saved & verified ✓` : `Saved, but verification failed: ${v.data?.error || v.data?.message || 'invalid key'}`);
                  } else {
                    alert(`Error: ${resp.error || 'Failed to save'}`);
                  }
                } catch (err) {
                  console.error('Error saving provider:', err);
                  alert('Failed to save provider configuration');
                } finally {
                  setSavingProvider(false);
                }
              }}
              disabled={savingProvider || !providerApiKey.trim() || (providerToConfig === 'zoominfo' && !providerClientId.trim())}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
            >
              {savingProvider && <Loader2 className="w-4 h-4 animate-spin" />}
              <Settings className="w-4 h-4" />
              Save &amp; Verify
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Outreach List Modal (cross-company) */}
      <Dialog open={showCreateListModal} onOpenChange={setShowCreateListModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Outreach List from Discovered Contacts</DialogTitle>
            <DialogDescription>
              Create a new cold outreach campaign populated with contacts discovered across your matched companies.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
              <input
                type="text"
                value={createListName}
                onChange={(e) => setCreateListName(e.target.value)}
                placeholder="e.g., Q1 Self-Insured Outreach"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Type</label>
                <select
                  value={newCampaignType}
                  onChange={(e) => setNewCampaignType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="cold_email">Cold Email</option>
                  <option value="cold_linkedin">Cold LinkedIn</option>
                  <option value="multi_channel">Multi-Channel</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Confidence</label>
                <select
                  value={createListMinConfidence}
                  onChange={(e) => setCreateListMinConfidence(parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="0">Any confidence</option>
                  <option value="0.5">50%+</option>
                  <option value="0.6">60%+</option>
                  <option value="0.7">70%+</option>
                  <option value="0.8">80%+</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={createListDMOnly}
                  onChange={(e) => setCreateListDMOnly(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Decision makers only
              </label>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
              {selectedCompanyIds.size > 0 ? (
                <p>This will create a campaign from qualifying discovered contacts at the <strong>{selectedCompanyIds.size.toLocaleString()}</strong> selected {selectedCompanyIds.size === 1 ? 'company' : 'companies'}{stageFilter ? ` (${getStageBadge(stageFilter).label})` : ''}.</p>
              ) : (
                <p>This will create a campaign with all qualifying discovered contacts across your {totalCount.toLocaleString()} matched companies. Tip: filter by the <strong>Outreach-ready</strong> stage and “Select all matching” to campaign just that cohort.</p>
              )}
              <p className="mt-1 text-gray-400">Contacts without email addresses are automatically excluded.</p>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowCreateListModal(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!createListName.trim()) return;
                setCreatingList(true);
                try {
                  const selectedIds = Array.from(selectedCompanyIds);
                  const resp = await leadBuilderApi.createOutreachList({
                    campaign_name: createListName.trim(),
                    campaign_type: newCampaignType,
                    decision_makers_only: createListDMOnly,
                    min_confidence: createListMinConfidence > 0 ? createListMinConfidence : undefined,
                    require_email: true,
                    company_match_ids: selectedIds.length > 0 ? selectedIds : undefined,
                  });
                  if (resp.success && resp.data) {
                    setShowCreateListModal(false);
                    setSelectedCompanyIds(new Set());
                    alert(
                      `Outreach list "${createListName}" created with ${resp.data.prospects_added} prospects` +
                      `${selectedIds.length > 0 ? ` from ${selectedIds.length} selected companies` : ''}. ` +
                      `Tracking it in Outreach Lists.`
                    );
                    setActiveTab('outreach-lists');
                  } else {
                    alert(`Error: ${resp.error || 'Failed to create outreach list'}`);
                  }
                } catch (err) {
                  console.error('Error creating outreach list:', err);
                  alert('Failed to create outreach list');
                } finally {
                  setCreatingList(false);
                }
              }}
              disabled={creatingList || !createListName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
            >
              {creatingList && <Loader2 className="w-4 h-4 animate-spin" />}
              <Mail className="w-4 h-4" />
              Create Outreach List
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// OUTREACH LISTS TAB — trackable "move to cold outreach" entities
// ============================================================================

const OUTREACH_LIST_STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  enriching: 'bg-amber-100 text-amber-700',
  ready: 'bg-blue-100 text-blue-700',
  in_campaign: 'bg-green-100 text-green-700',
  archived: 'bg-gray-100 text-gray-400',
};

function OutreachListsTab({ onGoToCompanies }: { onGoToCompanies: () => void }) {
  const [lists, setLists] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [buildingQueueId, setBuildingQueueId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await leadBuilderApi.getOutreachLists();
      if (resp.success && resp.data) setLists(resp.data);
    } catch (err) {
      console.error('Failed to load outreach lists:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id: string) => {
    if (expandedId === id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const resp = await leadBuilderApi.getOutreachList(id);
      if (resp.success && resp.data) setDetail(resp.data);
    } catch (err) {
      console.error('Failed to load list detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const buildCallQueue = async (l: any) => {
    if (!l.campaign_id) return;
    setBuildingQueueId(l.id);
    try {
      const resp = await coldOutreachApi.buildCallQueue(l.campaign_id, { assign_to_me: true });
      if (resp.success && resp.data) {
        const n = resp.data.call_tasks_created ?? 0;
        alert(
          n > 0
            ? `Built a call queue of ${n} prospect${n === 1 ? '' : 's'}, assigned to you. Work it from Cold Outreach → Phone.`
            : 'No new call tasks — prospects may lack phone numbers or already have open tasks.',
        );
      } else {
        alert(`Error: ${resp.error || 'Failed to build call queue'}`);
      }
    } catch (err) {
      console.error('Build call queue failed:', err);
      alert('Failed to build call queue');
    } finally {
      setBuildingQueueId(null);
    }
  };

  const archive = async (id: string) => {
    try {
      await leadBuilderApi.updateOutreachList(id, { status: 'archived' });
      load();
    } catch (err) { console.error('Archive failed:', err); }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this outreach list? The linked campaign is not deleted.')) return;
    try {
      await leadBuilderApi.deleteOutreachList(id);
      if (expandedId === id) { setExpandedId(null); setDetail(null); }
      load();
    } catch (err) { console.error('Delete failed:', err); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading outreach lists…</div>;
  }

  if (lists.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <Mail className="w-8 h-8 mx-auto text-gray-300 mb-2" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">No outreach lists yet</h3>
        <p className="text-sm text-gray-500 mb-4">
          Select companies in Matched Companies and use “Move to Cold Outreach” to create a tracked list.
        </p>
        <button onClick={onGoToCompanies} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
          Go to Matched Companies
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Outreach Lists</h2>
        <button onClick={load} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
          <Loader2 className="w-3 h-3" /> Refresh
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-100">
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium text-right">Companies</th>
            <th className="px-4 py-2 font-medium text-right">Contacts</th>
            <th className="px-4 py-2 font-medium text-right">Prospects</th>
            <th className="px-4 py-2 font-medium">Created</th>
            <th className="px-4 py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {lists.map((l) => (
            <React.Fragment key={l.id}>
              <tr className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(l.id)}>
                <td className="px-4 py-2.5 font-medium text-gray-900">
                  {l.name}
                  {l.list_source === 'contact_list' && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 align-middle" title="Created via “Add to a contact list” — members live in the nurture contact list system">
                      contact list
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${OUTREACH_LIST_STATUS_STYLES[l.status] || 'bg-gray-100 text-gray-700'}`}>
                    {String(l.status).replace('_', ' ')}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">{l.company_count}</td>
                <td className="px-4 py-2.5 text-right">{l.contact_count}</td>
                <td className="px-4 py-2.5 text-right">{l.prospects_added}</td>
                <td className="px-4 py-2.5 text-gray-500">{l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                  {l.campaign_id && (
                    <button
                      onClick={() => buildCallQueue(l)}
                      disabled={buildingQueueId === l.id}
                      className="text-xs font-medium text-green-700 hover:text-green-900 px-2 disabled:opacity-50"
                      title="Create phone call tasks for this list's prospects and assign them to you"
                    >
                      {buildingQueueId === l.id ? 'Building…' : 'Build call queue'}
                    </button>
                  )}
                  {l.list_source !== 'contact_list' && l.status !== 'archived' && (
                    <button onClick={() => archive(l.id)} className="text-xs text-gray-500 hover:text-gray-800 px-2">Archive</button>
                  )}
                  {l.list_source !== 'contact_list' && (
                    <button onClick={() => remove(l.id)} className="text-xs text-red-500 hover:text-red-700 px-2">Delete</button>
                  )}
                </td>
              </tr>
              {expandedId === l.id && (
                <tr className="bg-gray-50/60">
                  <td colSpan={7} className="px-4 py-3">
                    {detailLoading ? (
                      <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading members…</div>
                    ) : detail && detail.list_source === 'contact_list' ? (
                      <div>
                        <p className="text-xs text-gray-600 mb-2">
                          Members imported from the lead builder into a nurture contact list.
                        </p>
                        <div className="max-h-64 overflow-y-auto rounded border border-gray-200 bg-white">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-gray-400 border-b">
                                <th className="px-3 py-1.5">Name</th>
                                <th className="px-3 py-1.5">Email</th>
                                <th className="px-3 py-1.5">Company</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(detail.contacts || []).map((c: any) => (
                                <tr key={c.id} className="border-b last:border-0">
                                  <td className="px-3 py-1.5 text-gray-800">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                                  <td className="px-3 py-1.5 text-gray-500">{c.email || '—'}</td>
                                  <td className="px-3 py-1.5 text-gray-500">{c.company_name || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : detail ? (
                      <div>
                        {detail.campaign_id && (
                          <p className="text-xs text-gray-600 mb-2">Linked campaign: <code className="bg-gray-100 px-1 rounded">{detail.campaign_id}</code></p>
                        )}
                        <div className="max-h-64 overflow-y-auto rounded border border-gray-200 bg-white">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-gray-400 border-b">
                                <th className="px-3 py-1.5">Company</th>
                                <th className="px-3 py-1.5">Domain</th>
                                <th className="px-3 py-1.5 text-right">Score</th>
                                <th className="px-3 py-1.5 text-right">Contacts</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(detail.members || []).map((m: any) => (
                                <tr key={m.company_match_id} className="border-b last:border-0">
                                  <td className="px-3 py-1.5 text-gray-800">{m.company_name}</td>
                                  <td className="px-3 py-1.5 text-gray-500">{m.domain || '—'}</td>
                                  <td className="px-3 py-1.5 text-right">{m.score != null ? Math.round(m.score) : '—'}</td>
                                  <td className="px-3 py-1.5 text-right">{m.contact_count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No detail available.</p>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default LeadBuilderPage;